import React, { useState, useMemo, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import {
  Play,
  Clock,
  RefreshCw,
  Settings,
  AlertCircle,
  CheckCircle,
  Database,
  Loader2,
  Car,
  Trash2,
  ChevronDown,
  ChevronRight,
  RotateCw
} from 'lucide-react';
import {
  useDataCollectionTasks,
  useRunDataCollectionTask,
  useExecutionHistory,
  useSyncRunStatus,
  useCleanupStaleStatuses
} from '@/hooks/useDataCollection';
import { useQueryClient } from '@tanstack/react-query';
import { useCreateVINJob, useVINJobsHistory } from '@/hooks/useVINProcessing';
import { useAllTenants } from '@/hooks/useTenants';
import {
  DataCollectionTask,
  dataCollectionAPI,
  CleanupStaleStatusesRequest
} from '@/services/api/dataCollection';
import { APIError } from '@/lib/api/authenticatedFetch';
import { DatePicker } from '@/components/ui/date-picker';
import { useAuth } from '@/features/auth/contexts/EnhancedAuthContext';
import { CreateJobRequest, VINSamplingOptions, vinProcessorAPI } from '@/services/api/vinProcessor';
import { utcToESTDateString } from '@/lib/utils';
import { useApifyStatusPolling } from '../hooks/useApifyStatusPolling';
import { useManualApifySync } from '../hooks/useManualApifySync';

// Make options for VIN processing
const makeOptions = [
  { value: 'ford', label: 'Ford/Lincoln' },
  { value: 'gm', label: 'General Motors' },
  { value: 'hyundai', label: 'Hyundai' },
  { value: 'kia', label: 'Kia' },
  { value: 'stellantis', label: 'Stellantis' }
];

// Canonical OEM normalization — maps aliases to a single key
// Fixes mismatch: tenants use "General Motors", tasks use "gm"
const normalizeOEM = (oem: string): string => {
  const lower = oem.toLowerCase().trim();
  const aliases: Record<string, string> = {
    'general motors': 'gm',
    chevrolet: 'gm',
    chevy: 'gm',
    buick: 'gm',
    cadillac: 'gm',
    gmc: 'gm'
  };
  return aliases[lower] || lower;
};

// Helper to get OEM from task name (for execution display)
const getOEMFromTaskName = (taskName: string): string => {
  const lower = taskName.toLowerCase();
  if (lower.includes('gm') || lower.includes('general motors') || lower.includes('chevrolet'))
    return 'General Motors';
  if (lower.includes('stellantis')) return 'Stellantis';
  if (lower.includes('hyundai')) return 'Hyundai';
  if (lower.includes('kia')) return 'Kia';
  if (lower.includes('ford') || lower.includes('lincoln')) return 'Ford/Lincoln';
  return 'Unknown';
};

// Determine OEM from task ID/name
const getOEMFromTask = (task: DataCollectionTask): string => {
  // Prefer the authoritative oem field from the database
  if (task.oem) return normalizeOEM(task.oem);

  // Fallback: parse from task name/ID
  const combined = `${task.task_id || ''} ${task.name || ''}`.trim().toLowerCase();
  if (
    combined.includes('gm') ||
    combined.includes('general motors') ||
    combined.includes('chevrolet')
  )
    return 'gm';
  if (combined.includes('stellantis')) return 'stellantis';
  if (combined.includes('hyundai')) return 'hyundai';
  if (combined.includes('kia')) return 'kia';
  return 'ford';
};

// Get OEM label
const getOEMLabel = (oem: string): string => {
  switch (normalizeOEM(oem)) {
    case 'gm':
      return 'General Motors';
    case 'stellantis':
      return 'Stellantis';
    case 'hyundai':
      return 'Hyundai';
    case 'kia':
      return 'Kia';
    case 'ford':
    default:
      return 'Ford/Lincoln';
  }
};

import { WorkflowHealthDashboard } from './WorkflowHealthDashboard';
import { WorkflowDefinitionCard } from './WorkflowDefinitionCard';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import {
  normalizeVINProcessingExecution,
  normalizeDataCollectionExecution
} from '@/types/executionHistory';
import {
  normalizeStatus,
  isCompletedStatus,
  isPartialSuccessStatus,
  isFailedStatus,
  isRunningStatus,
  isFinishedStatus,
  isCancelledStatus
} from '@/utils/executionStatus';

export const WorkflowManagementTab: React.FC = () => {
  const { toast } = useToast();
  const { user } = useAuth();

  // Data Collection state
  const [refreshDialogOpen, setRefreshDialogOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<string>(''); // Selected tenant ID
  const [selectedAreas, setSelectedAreas] = useState<Set<string>>(new Set());
  const [selectedOEM, setSelectedOEM] = useState<string>('ford');
  const [activeRunIds, setActiveRunIds] = useState<Set<string>>(new Set()); // Track active runs for polling
  const [activeJobIds, setActiveJobIds] = useState<Set<string>>(new Set()); // Track active VIN processing jobs for polling
  const [forceRun, setForceRun] = useState<boolean>(false); // Force run even if Apify shows active runs
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set()); // Track expanded date/task sections
  const [collapsedByDefault, setCollapsedByDefault] = useState(true); // Collapse execution details by default
  const [visibleExecutions, setVisibleExecutions] = useState(10); // Start with showing only 10 executions
  const [refreshingJobId, setRefreshingJobId] = useState<string | null>(null); // Track refresh state
  const [rerunningExecutionId, setRerunningExecutionId] = useState<string | null>(null); // Track re-run state
  const [rerunConfirmDialogOpen, setRerunConfirmDialogOpen] = useState(false); // Confirmation dialog state
  const [executionToRerun, setExecutionToRerun] = useState<any | null>(null); // Execution to re-run

  // Lazy loading state - delay execution history loading for instant page load
  const [enableHistoryLoading, setEnableHistoryLoading] = useState(false);

  // VIN Processing state
  const [processDialogOpen, setProcessDialogOpen] = useState(false);
  const [configureDialogOpen, setConfigureDialogOpen] = useState(false);
  const [inventoryDate, setInventoryDate] = useState<Date | undefined>(undefined);
  const [selectedMake, setSelectedMake] = useState<string>('');
  const [inputMode, setInputMode] = useState<
    'inventory_date_make' | 'step1_execution' | 'apify_run_id'
  >('inventory_date_make');
  const [selectedRunId, setSelectedRunId] = useState<string>('');
  const [apifyRunId, setApifyRunId] = useState<string>('');

  // VIN Sampling options
  const [samplingStrategy, setSamplingStrategy] = useState<'all' | 'random' | 'first' | 'last'>(
    'all'
  );
  const [samplingCount, setSamplingCount] = useState<number>(100);
  const [forceRerun, setForceRerun] = useState<boolean>(false);

  // Configuration state (saved preferences)
  const [configuredRunId, setConfiguredRunId] = useState<string>(''); // Pre-configured execution
  const [configuredApifyRunId, setConfiguredApifyRunId] = useState<string>(''); // Pre-configured Apify run ID
  const [configuredDate, setConfiguredDate] = useState<Date | undefined>(undefined); // Pre-configured date
  const [configuredMake, setConfiguredMake] = useState<string>(''); // Pre-configured make
  const [configuredInputMode, setConfiguredInputMode] = useState<
    'inventory_date_make' | 'step1_execution' | 'apify_run_id'
  >('inventory_date_make'); // Pre-configured mode
  const [configuredSamplingStrategy, setConfiguredSamplingStrategy] = useState<
    'all' | 'random' | 'first' | 'last'
  >('all');
  const [configuredSamplingCount, setConfiguredSamplingCount] = useState<number>(100);
  const [configuredForceRerun, setConfiguredForceRerun] = useState<boolean>(false);

  // API hooks
  const queryClient = useQueryClient();
  const { data: tenants, isLoading: tenantsLoading, error: tenantsError } = useAllTenants();
  const {
    data: tasks,
    isLoading: tasksLoading,
    error: tasksError
  } = useDataCollectionTasks(selectedTenant || null);
  const runTaskMutation = useRunDataCollectionTask();
  const createVINJobMutation = useCreateVINJob();
  const {
    data: executionHistory,
    isLoading: historyLoading,
    error: historyError,
    refetch: refetchDataCollection
  } = useExecutionHistory(
    100, // Increased to 100 to show more executions, especially for demo tenant
    activeRunIds,
    {
      // Don't set any date filter to get all available data
    },
    { enabled: enableHistoryLoading } // Lazy load - only fetch when enabled
  );
  const {
    data: vinProcessingHistory,
    isLoading: vinProcessingHistoryLoading,
    error: vinProcessingHistoryError,
    refetch: refetchVINProcessing
  } = useVINJobsHistory(
    {
      limit: 5, // Reduced from 25 to 5 for instant loading - can load more on demand
      tenant_id: selectedTenant || undefined
    },
    activeJobIds,
    { enabled: enableHistoryLoading } // Lazy load - only fetch when enabled
  );

  // Debug current API state (commented out to reduce console noise)
  // console.log('WorkflowManagementTab API State:', {
  //   dataCollection: {
  //     loading: historyLoading,
  //     error: !!historyError,
  //     count: executionHistory?.length
  //   },
  //   vinProcessing: {
  //     loading: vinProcessingHistoryLoading,
  //     error: !!vinProcessingHistoryError,
  //     count: vinProcessingHistory?.executions?.length
  //   },
  //   errors: {
  //     historyError: historyError?.message,
  //     vinProcessingHistoryError: vinProcessingHistoryError?.message
  //   }
  // });
  const syncRunStatusMutation = useSyncRunStatus();
  const cleanupStaleStatusesMutation = useCleanupStaleStatuses();

  // Manual Apify sync for specific runs
  const { syncSpecificRun, syncing: syncingRunId } = useManualApifySync();

  // Function to refresh Cloud Run status for a stuck VIN processing job
  const handleRefreshJobStatus = async (jobId: string) => {
    setRefreshingJobId(jobId);
    try {
      console.log('Refreshing job status for:', jobId);
      const result = await vinProcessorAPI.refreshCloudRunStatus(jobId);
      console.log('Refresh result:', result);

      toast({
        title: 'Status Refreshed',
        description: `Cloud Run status refreshed for job ${jobId.slice(0, 8)}`
      });

      // Force cache invalidation and refetch
      queryClient.invalidateQueries({ queryKey: ['vinProcessing', 'history'] });
      queryClient.invalidateQueries({ queryKey: ['dataCollection', 'executionHistory'] });

      // Wait a moment before refetching to allow backend to update
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Refetch execution history to get updated status
      await refetchDataCollection();
      await refetchVINProcessing();

      console.log('Refetch completed for job:', jobId);
    } catch (error) {
      console.error('Job refresh failed:', error);
      toast({
        title: 'Refresh Failed',
        description: error instanceof Error ? error.message : 'Failed to refresh job status',
        variant: 'destructive'
      });
    } finally {
      setRefreshingJobId(null);
    }
  };

  // Auto-select tenant if user has only one tenant
  useEffect(() => {
    if (!tenantsLoading && tenants && tenants.length > 0 && !selectedTenant) {
      // If user has only one tenant, auto-select it
      if (tenants.length === 1 && tenants[0]) {
        setSelectedTenant(tenants[0].id);
      }
    }
  }, [tenants, tenantsLoading, selectedTenant]);

  // Enable history loading after a short delay to allow page to render instantly
  // This provides instant UI feedback while data loads in background
  useEffect(() => {
    // Enable history loading after 100ms delay for instant page render
    const timer = setTimeout(() => {
      setEnableHistoryLoading(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Restore VIN processing configuration from localStorage on mount
  useEffect(() => {
    try {
      const storedConfig = localStorage.getItem('vin_processing_config');
      if (storedConfig) {
        const config = JSON.parse(storedConfig);

        if (config.inputMode) {
          setConfiguredInputMode(config.inputMode);
        }
        if (config.runId) {
          setConfiguredRunId(config.runId);
        }
        if (config.apifyRunId) {
          setConfiguredApifyRunId(config.apifyRunId);
        }
        if (config.date) {
          setConfiguredDate(new Date(config.date));
        }
        if (config.make) {
          setConfiguredMake(config.make);
        }
        if (config.samplingStrategy) {
          setConfiguredSamplingStrategy(config.samplingStrategy);
        }
        if (config.samplingCount) {
          setConfiguredSamplingCount(config.samplingCount);
        }
        if (config.forceRerun !== undefined) {
          setConfiguredForceRerun(config.forceRerun);
        }
      }
    } catch (error) {
      // Clear invalid config
      localStorage.removeItem('vin_processing_config');
    }
  }, []);

  // Restore active run IDs from localStorage on mount
  // DEFERRED: Status check deferred to avoid blocking initial page load
  useEffect(() => {
    let deferredTimer: ReturnType<typeof setTimeout> | null = null;

    const stored = localStorage.getItem('workflow_active_runs');
    if (stored) {
      try {
        const activeRuns: Array<{ runId: string; timestamp: number }> = JSON.parse(stored);
        // Filter out runs older than 2 hours
        const twoHoursAgo = Date.now() - 2 * 60 * 60 * 1000;
        const recentRuns = activeRuns.filter(run => run.timestamp > twoHoursAgo);

        if (recentRuns.length > 0) {
          const restoredRunIds = new Set(recentRuns.map(r => r.runId));
          setActiveRunIds(restoredRunIds);

          // Check status of each restored run - DEFERRED by 3 seconds for instant page load
          deferredTimer = setTimeout(() => {
            const checkRestoredRuns = async () => {
              const stillActive: string[] = [];

              for (const runId of Array.from(restoredRunIds)) {
                try {
                  const status = await dataCollectionAPI.getTaskStatus(runId);
                  if (status && isFinishedStatus(status.status)) {
                    // Run is complete, remove from active set
                    restoredRunIds.delete(runId);
                    setActiveRunIds(prev => {
                      const updated = new Set(prev);
                      updated.delete(runId);
                      return updated;
                    });
                  } else {
                    stillActive.push(runId);
                  }
                } catch (error) {
                  // If we can't check status, assume it's still active
                  stillActive.push(runId);
                }
              }

              // Update localStorage with only still-active runs
              if (stillActive.length > 0) {
                const updatedRuns = stillActive.map(runId => ({
                  runId,
                  timestamp: activeRuns.find(r => r.runId === runId)?.timestamp || Date.now()
                }));
                localStorage.setItem('workflow_active_runs', JSON.stringify(updatedRuns));
              } else {
                localStorage.removeItem('workflow_active_runs');
              }
            };

            checkRestoredRuns();
          }, 3000); // 3 second delay for instant page load
        } else {
          // All runs are old, clear localStorage
          localStorage.removeItem('workflow_active_runs');
        }
      } catch (error) {
        localStorage.removeItem('workflow_active_runs');
      }
    }

    return () => {
      if (deferredTimer) clearTimeout(deferredTimer);
    };
  }, []);

  // Save active run IDs to localStorage whenever they change
  useEffect(() => {
    if (activeRunIds.size > 0) {
      const activeRuns = Array.from(activeRunIds).map(runId => ({
        runId,
        timestamp: Date.now()
      }));
      localStorage.setItem('workflow_active_runs', JSON.stringify(activeRuns));
    } else {
      // Clear localStorage when no active runs
      localStorage.removeItem('workflow_active_runs');
    }
  }, [activeRunIds]);

  // Clean up active run IDs based on execution history status
  // This ensures runs are removed from activeRunIds when they complete, even if polling misses them
  // Only runs after history is loaded (not on initial mount)
  useEffect(() => {
    if (
      !enableHistoryLoading ||
      !executionHistory ||
      executionHistory.length === 0 ||
      activeRunIds.size === 0
    ) {
      return;
    }

    const activeRunIdsArray = Array.from(activeRunIds);
    const completedRunIds: string[] = [];

    activeRunIdsArray.forEach(runId => {
      // Find this run in execution history
      const exec = executionHistory.find(e => e.run_id === runId);
      if (exec && isFinishedStatus(exec.status)) {
        completedRunIds.push(runId);
      }
    });

    // Remove completed runs from activeRunIds
    if (completedRunIds.length > 0) {
      setActiveRunIds(prev => {
        const updated = new Set(prev);
        completedRunIds.forEach(runId => updated.delete(runId));
        return updated;
      });

      // Also invalidate execution history to refresh UI
      queryClient.invalidateQueries({ queryKey: ['dataCollection', 'executionHistory'] });
    }
  }, [executionHistory, activeRunIds, queryClient, enableHistoryLoading]);

  // Periodic check of active runs against backend API to catch stale status
  // This is a fallback in case execution history is stale or doesn't include the run
  // DEFERRED: Delay this check to avoid blocking initial page load
  useEffect(() => {
    if (activeRunIds.size === 0) {
      return;
    }

    let intervalId: ReturnType<typeof setInterval> | null = null;

    // Defer status checking by 2 seconds to allow page to load first
    const deferredTimer = setTimeout(() => {
      const checkActiveRunsStatus = async () => {
        const activeRunIdsArray = Array.from(activeRunIds);
        const completedRunIds: string[] = [];

        // Check each active run's status directly from the backend API
        await Promise.allSettled(
          activeRunIdsArray.map(async runId => {
            try {
              const status = await dataCollectionAPI.getTaskStatus(runId);
              if (status && isFinishedStatus(status.status)) {
                completedRunIds.push(runId);
              }
            } catch (error) {
              // Silently handle errors - don't remove runs if we can't check status
              console.debug(`Could not check status for run ${runId}:`, error);
            }
          })
        );

        // Remove completed runs from activeRunIds
        if (completedRunIds.length > 0) {
          setActiveRunIds(prev => {
            const updated = new Set(prev);
            completedRunIds.forEach(runId => updated.delete(runId));
            return updated;
          });

          // Invalidate execution history to refresh UI
          queryClient.invalidateQueries({ queryKey: ['dataCollection', 'executionHistory'] });
        }
      };

      // Check after delay, then every 30 seconds
      checkActiveRunsStatus();
      intervalId = setInterval(checkActiveRunsStatus, 30 * 1000);
    }, 2000); // 2 second delay for instant page load

    return () => {
      clearTimeout(deferredTimer);
      if (intervalId) clearInterval(intervalId);
    };
  }, [activeRunIds, queryClient]);

  // Verify and fix stale "running" statuses in execution history
  // This checks runs that appear as "running" but might actually be completed
  // DEFERRED: Delay this check to avoid blocking initial page load
  useEffect(() => {
    if (!executionHistory || executionHistory.length === 0) {
      return;
    }

    let intervalId: ReturnType<typeof setInterval> | null = null;

    // Defer stale status verification by 5 seconds to allow page to load first
    const deferredTimer = setTimeout(() => {
      const verifyRunningStatuses = async () => {
        // Find runs that appear as "running" in execution history
        const potentiallyStaleRuns = executionHistory.filter(
          exec =>
            exec.run_id &&
            isRunningStatus(exec.status) &&
            // Only check runs that started more than 5 minutes ago (likely stale if still showing as running)
            exec.started_at &&
            Date.now() - new Date(exec.started_at).getTime() > 5 * 60 * 1000
        );

        if (potentiallyStaleRuns.length === 0) {
          return;
        }

        // Check status of each potentially stale run
        const staleRunIds: string[] = [];
        await Promise.allSettled(
          potentiallyStaleRuns.map(async exec => {
            if (!exec.run_id) return;
            try {
              const status = await dataCollectionAPI.getTaskStatus(exec.run_id);
              if (status && isFinishedStatus(status.status)) {
                staleRunIds.push(exec.run_id);
              }
            } catch (error) {
              // Silently handle errors
              console.debug(`Could not verify status for run ${exec.run_id}:`, error);
            }
          })
        );

        // If we found stale runs, refresh execution history
        if (staleRunIds.length > 0) {
          // Remove from activeRunIds if they're there
          setActiveRunIds(prev => {
            const updated = new Set(prev);
            staleRunIds.forEach(runId => updated.delete(runId));
            return updated;
          });

          // Invalidate execution history to force refresh
          queryClient.invalidateQueries({ queryKey: ['dataCollection', 'executionHistory'] });
        }
      };

      // Check every 60 seconds for stale running statuses
      intervalId = setInterval(verifyRunningStatuses, 60 * 1000);

      // Run after delay instead of immediately
      verifyRunningStatuses();
    }, 5000); // 5 second delay for instant page load

    return () => {
      clearTimeout(deferredTimer);
      if (intervalId) clearInterval(intervalId);
    };
  }, [executionHistory, queryClient]);

  // Clean up active job IDs based on VIN processing history status
  // This ensures jobs are removed from activeJobIds when they complete, even if polling misses them
  // Only runs after history is loaded (not on initial mount)
  useEffect(() => {
    if (
      !enableHistoryLoading ||
      !vinProcessingHistory?.executions ||
      vinProcessingHistory.executions.length === 0 ||
      activeJobIds.size === 0
    ) {
      return;
    }

    const activeJobIdsArray = Array.from(activeJobIds);
    const completedJobIds: string[] = [];

    activeJobIdsArray.forEach(jobId => {
      // Find this job in VIN processing history
      const exec = vinProcessingHistory.executions.find(e => e.job_id === jobId || e.id === jobId);
      if (exec && (isFinishedStatus(exec.status) || normalizeStatus(exec.status) === 'cancelled')) {
        completedJobIds.push(jobId);
      }
    });

    // Remove completed jobs from activeJobIds
    if (completedJobIds.length > 0) {
      setActiveJobIds(prev => {
        const updated = new Set(prev);
        completedJobIds.forEach(jobId => updated.delete(jobId));
        return updated;
      });

      // Also invalidate execution history to refresh UI
      queryClient.invalidateQueries({ queryKey: ['vinProcessing', 'history'] });
    }
  }, [vinProcessingHistory, activeJobIds, queryClient, enableHistoryLoading]);

  // Get latest execution for refresh inventory (Step 1)
  const latestRefreshExecution = useMemo(() => {
    if (!executionHistory || executionHistory.length === 0) return null;
    // Find latest execution for data collection tasks (not VIN processing)
    const refreshExecutions = executionHistory.filter(
      exec => exec.task_name && exec.task_name.includes('cargurus')
    );
    if (refreshExecutions.length === 0) return null;
    // Sort by started_at descending and get the most recent
    const sorted = refreshExecutions.sort(
      (a, b) =>
        (b.started_at ? new Date(b.started_at).getTime() : 0) -
        (a.started_at ? new Date(a.started_at).getTime() : 0)
    );
    return sorted[0];
  }, [executionHistory]);

  // Get latest execution for VIN processing (Step 2)
  const latestVINProcessingExecution = useMemo(() => {
    if (!vinProcessingHistory?.executions || vinProcessingHistory.executions.length === 0)
      return null;
    // Sort by started_at descending and get the most recent
    const sorted = [...vinProcessingHistory.executions].sort((a, b) => {
      const aTime = a.started_at ? new Date(a.started_at).getTime() : 0;
      const bTime = b.started_at ? new Date(b.started_at).getTime() : 0;
      return bTime - aTime;
    });
    return sorted[0];
  }, [vinProcessingHistory]);

  // Create unified execution history that merges both data collection and VIN processing
  const unifiedExecutionHistory = useMemo(() => {
    const unified: any[] = [];

    // Add data collection executions - use normalization function
    if (executionHistory && executionHistory.length > 0) {
      executionHistory.forEach(exec => {
        const normalized = normalizeDataCollectionExecution(exec);
        unified.push({
          ...normalized,
          // Ensure we have the fields needed for display
          run_id: normalized.run_id || normalized.id,
          task_name: normalized.task_name,
          workflow_name: normalized.workflow_name
        });
      });
    }

    // Add VIN processing executions - use normalization function to detect failed jobs
    if (vinProcessingHistory?.executions && vinProcessingHistory.executions.length > 0) {
      vinProcessingHistory.executions.forEach(exec => {
        // Use normalizeVINProcessingExecution to detect failed jobs marked as "running"
        const normalized = normalizeVINProcessingExecution(exec);

        // Map normalized execution to match data collection format for display
        unified.push({
          ...normalized,
          // Use job_id as run_id for display consistency
          run_id: normalized.job_id || normalized.id,
          // Map VIN processing fields for display
          task_name:
            normalized.entry_point === 'inventory_date_make'
              ? 'Process VINs from Inventory'
              : normalized.entry_point === 'step1_execution'
                ? 'Process VINs from Data Collection'
                : normalized.entry_point === 'apify_run_id'
                  ? 'Process VINs from Apify Run'
                  : 'VIN Processing',
          workflow_name: 'Step 2: Process VINs',
          records_processed: normalized.processed_vins || 0,
          // VIN processing doesn't have validation, so set to null
          validation_status: null,
          validation_summary: null,
          // VIN processing doesn't have GCS path in the same format
          gcs_path: null
        });
      });
    }

    // Sort by started_at descending (most recent first)
    unified.sort((a, b) => {
      const aTime = a.started_at ? new Date(a.started_at).getTime() : 0;
      const bTime = b.started_at ? new Date(b.started_at).getTime() : 0;
      return bTime - aTime;
    });

    // Return all executions - we'll paginate in groupedExecutionHistory
    return unified;
  }, [executionHistory, vinProcessingHistory]);

  // Group executions by date only - with pagination
  const groupedExecutionHistory = useMemo(() => {
    if (!unifiedExecutionHistory || unifiedExecutionHistory.length === 0) return [];

    // Limit the executions for better performance
    const limitedExecutions = unifiedExecutionHistory.slice(0, visibleExecutions);

    const groups = new Map<string, any[]>(); // date -> executions

    limitedExecutions.forEach(exec => {
      // Get date from started_at, converted to EST timezone
      const date = exec.started_at ? utcToESTDateString(exec.started_at) : 'Unknown';

      if (!groups.has(date)) {
        groups.set(date, []);
      }
      groups.get(date)!.push(exec);
    });

    // Convert to array format and sort by date descending, and sort executions within each date group
    const result: Array<{
      date: string;
      executions: any[];
    }> = Array.from(groups.entries())
      .sort((a, b) => new Date(b[0]).getTime() - new Date(a[0]).getTime()) // Sort dates descending
      .map(([date, executions]) => {
        // Sort executions within each date group by started_at descending (most recent first)
        const sortedExecutions = [...executions].sort((a, b) => {
          const aTime = a.started_at ? new Date(a.started_at).getTime() : 0;
          const bTime = b.started_at ? new Date(b.started_at).getTime() : 0;
          return bTime - aTime;
        });
        return {
          date,
          executions: sortedExecutions
        };
      });

    return result;
  }, [unifiedExecutionHistory, visibleExecutions]);

  // Calculate global serial numbers for all executions across all date groups
  const executionSerialMap = useMemo(() => {
    const serialMap = new Map<string, number>();
    let serialNumber = 1;

    // Iterate through grouped executions in order (already sorted by date descending)
    groupedExecutionHistory.forEach(dateGroup => {
      // Within each date group, executions are already sorted by time descending
      dateGroup.executions.forEach(exec => {
        const execId = exec.id || exec.run_id || exec.job_id || exec.started_at || '';
        if (execId) {
          serialMap.set(execId, serialNumber);
          serialNumber++;
        }
      });
    });

    return serialMap;
  }, [groupedExecutionHistory]);

  // Poll Apify for status updates on long-running tasks
  const { pollingStatus, lastPoll, pollNow } = useApifyStatusPolling(
    unifiedExecutionHistory || [],
    {
      enabled: true,
      pollingInterval: 30000, // 30 seconds
      onStatusUpdate: () => {
        // Refetch execution history when a status is updated
        refetchDataCollection();
      }
    }
  );

  // Get completed Step 1 executions for selection
  const completedStep1Executions = useMemo(() => {
    if (!executionHistory || executionHistory.length === 0) {
      return [];
    }

    // Filter for completed data collection tasks (Step 1)
    // Handle various status formats: 'completed', 'success', 'SUCCEEDED', etc.
    // Note: Removed 'cargurus' filter to include all data collection tasks (e.g., Pensacola)
    const filtered = executionHistory.filter(exec => {
      // Step 1 executions are data collection tasks - they have task_name but no entry_point
      // Step 2 executions are VIN processing - they have entry_point (e.g., 'step1_execution', 'inventory_date_make')
      // Also check workflow_name to distinguish Step 1 from Step 2
      const isStep1Execution =
        exec.task_name && // Has task_name (data collection task)
        !exec.entry_point && // No entry_point (not a VIN processing job)
        exec.workflow_name !== 'Step 2: Process VINs'; // Not a VIN processing workflow

      if (!isStep1Execution) return false;

      // Check status - handle various status formats
      return isCompletedStatus(exec.status);
    });

    // Deduplicate by unique identifier
    // Strategy: Create a composite key from id (if available) and run_id
    // This ensures we catch duplicates whether they have id, run_id, or both
    const seen = new Map<string, (typeof filtered)[0]>();
    const seenKeys = new Set<string>();

    for (const exec of filtered) {
      // Create a unique key: prefer id if available, otherwise use run_id
      // If both are missing, skip this execution (shouldn't happen, but safety check)
      const uniqueKey = exec.id || exec.run_id;
      if (!uniqueKey) {
        continue; // Skip executions without any identifier
      }

      // Check if we've seen this key before
      if (seenKeys.has(uniqueKey)) {
        // Duplicate found - keep the one with more recent started_at
        const existing = seen.get(uniqueKey);
        if (existing) {
          const existingTime = existing.started_at ? new Date(existing.started_at).getTime() : 0;
          const currentTime = exec.started_at ? new Date(exec.started_at).getTime() : 0;
          if (currentTime > existingTime) {
            // Current execution is more recent, replace it
            seen.set(uniqueKey, exec);
          }
          // Otherwise keep the existing one
        }
      } else {
        // First time seeing this key, add it
        seen.set(uniqueKey, exec);
        seenKeys.add(uniqueKey);
      }
    }

    // Convert back to array, sort, and limit
    return Array.from(seen.values())
      .sort(
        (a, b) =>
          (b.started_at ? new Date(b.started_at).getTime() : 0) -
          (a.started_at ? new Date(a.started_at).getTime() : 0)
      )
      .slice(0, 20); // Limit to last 20 completed executions
  }, [executionHistory]);

  // Note: We can't use hooks conditionally in a map, so we'll poll manually
  // The polling logic is handled in handleRefreshInventory

  // Group tasks by OEM, then by task name/ID (which represents the area)
  const tasksByOEMAndTask = useMemo(() => {
    if (!tasks || !Array.isArray(tasks)) return new Map<string, DataCollectionTask[]>();

    const grouped = new Map<string, DataCollectionTask[]>();
    tasks.forEach((task: DataCollectionTask) => {
      const oem = getOEMFromTask(task);

      if (!grouped.has(oem)) {
        grouped.set(oem, []);
      }
      grouped.get(oem)!.push(task);
    });

    return grouped;
  }, [tasks]);

  // Get selected tenant info
  const selectedTenantData = tenants?.find(t => t.id === selectedTenant);

  // Tenant's OEMs (prefer oems array, fallback to single oem) - normalized via canonical aliases
  // Supports multi-OEM tenants (e.g. Lakeland Automall: [Ford, Hyundai])
  const tenantOEMs = useMemo(() => {
    const t = selectedTenantData;
    if (!t) return null;
    if (t.oems && t.oems.length > 0) {
      return t.oems.map((o: string) => normalizeOEM(o)).filter(Boolean);
    }
    if (t.oem) return [normalizeOEM(t.oem)];
    return null;
  }, [selectedTenantData]);

  // Get tasks for selected OEM (from dropdown)
  const tasksForSelectedOEM = useMemo(() => {
    return tasksByOEMAndTask.get(selectedOEM) || [];
  }, [tasksByOEMAndTask, selectedOEM]);

  // Tasks to show for area list: use selected OEM when allowed for tenant
  // - No tenant OEMs (demo/internal): show tasks for selected OEM
  // - Tenant has OEMs: show tasks for selected OEM only when selectedOEM is in tenant's OEMs
  const tasksToUse = useMemo(() => {
    if (tenantOEMs === null) {
      return tasksForSelectedOEM;
    }
    return tenantOEMs.includes(selectedOEM) ? tasksForSelectedOEM : [];
  }, [tenantOEMs, selectedOEM, tasksForSelectedOEM]);

  // All tasks tenant can run (for Step 1 card enable/disable) - union across tenant's OEMs
  const tasksAvailableForTenant = useMemo(() => {
    if (!tasks || !Array.isArray(tasks)) return [];
    if (tenantOEMs === null) return tasks; // demo/internal: all tasks
    return tasks.filter((t: DataCollectionTask) => tenantOEMs.includes(getOEMFromTask(t)));
  }, [tasks, tenantOEMs]);

  // Unique area identifiers for the tasks we're showing
  const availableAreas = useMemo(() => {
    const areaSet = new Set<string>();
    tasksToUse.forEach(task => {
      const areaId = task.task_id || task.name || 'Unknown';
      areaSet.add(areaId);
    });
    return Array.from(areaSet).sort();
  }, [tasksToUse]);

  // Get tasks for a given area - use same source as availableAreas for consistency
  const getTasksForArea = (areaId: string): DataCollectionTask[] => {
    return tasksToUse.filter(task => (task.task_id || task.name) === areaId);
  };

  const handleOpenRefreshDialog = () => {
    // Don't reset tenant - use the one selected at top level
    if (!selectedTenant) {
      toast({
        title: 'Tenant Required',
        description: 'Please select a tenant first before configuring workflow steps.',
        variant: 'destructive'
      });
      return;
    }
    setSelectedAreas(new Set());
    // Default to first tenant OEM if configured, otherwise default to 'ford'
    const defaultOEM = tenantOEMs && tenantOEMs.length > 0 ? tenantOEMs[0] : 'ford';
    setSelectedOEM(defaultOEM);
    setForceRun(false); // Reset force option
    setRefreshDialogOpen(true);
  };

  const handleOpenProcessDialog = () => {
    // Check tenant selection
    if (!selectedTenant) {
      toast({
        title: 'Tenant Required',
        description: 'Please select a tenant first before processing VINs.',
        variant: 'destructive'
      });
      return;
    }
    // If configuration exists, use it directly
    if (configuredInputMode === 'step1_execution' && configuredRunId) {
      setSelectedRunId(configuredRunId);
      setInputMode('step1_execution');
      setSamplingStrategy(configuredSamplingStrategy);
      setSamplingCount(configuredSamplingCount);
      // Auto-start processing with configured execution
      handleProcessVINs();
    } else if (configuredInputMode === 'apify_run_id' && configuredApifyRunId) {
      setApifyRunId(configuredApifyRunId);
      setInputMode('apify_run_id');
      setSamplingStrategy(configuredSamplingStrategy);
      setSamplingCount(configuredSamplingCount);
      // Auto-start processing with configured Apify run ID
      handleProcessVINs();
    } else if (configuredInputMode === 'inventory_date_make' && configuredDate && configuredMake) {
      // Use configured date/make
      setInventoryDate(configuredDate);
      setSelectedMake(configuredMake);
      setInputMode('inventory_date_make');
      setSamplingStrategy(configuredSamplingStrategy);
      setSamplingCount(configuredSamplingCount);
      // Auto-start processing with configured date/make
      handleProcessVINs();
    } else {
      // Open dialog for configuration
      // Load configured values if available, otherwise reset
      if (configuredInputMode === 'step1_execution' && configuredRunId) {
        setInputMode('step1_execution');
        setSelectedRunId(configuredRunId);
      } else if (configuredInputMode === 'apify_run_id' && configuredApifyRunId) {
        setInputMode('apify_run_id');
        setApifyRunId(configuredApifyRunId);
      } else if (
        configuredInputMode === 'inventory_date_make' &&
        configuredDate &&
        configuredMake
      ) {
        setInputMode('inventory_date_make');
        setInventoryDate(configuredDate);
        setSelectedMake(configuredMake);
      } else {
        setInputMode('inventory_date_make');
        setInventoryDate(undefined);
        setSelectedMake('');
        setSelectedRunId('');
        setApifyRunId('');
      }
      setSamplingStrategy(configuredSamplingStrategy);
      setSamplingCount(configuredSamplingCount);
      setProcessDialogOpen(true);
    }
  };

  const handleOpenConfigureDialog = () => {
    // Check tenant selection
    if (!selectedTenant) {
      toast({
        title: 'Tenant Required',
        description: 'Please select a tenant first before configuring workflow steps.',
        variant: 'destructive'
      });
      return;
    }
    // Load configured values into dialog state
    if (configuredInputMode === 'step1_execution' && configuredRunId) {
      setInputMode('step1_execution');
      setSelectedRunId(configuredRunId);
    } else if (configuredInputMode === 'apify_run_id' && configuredApifyRunId) {
      setInputMode('apify_run_id');
      setApifyRunId(configuredApifyRunId);
    } else if (configuredInputMode === 'inventory_date_make' && configuredDate && configuredMake) {
      setInputMode('inventory_date_make');
      setInventoryDate(configuredDate);
      setSelectedMake(configuredMake);
    } else {
      // Default to inventory_date_make mode if nothing configured
      setInputMode('inventory_date_make');
      setInventoryDate(undefined);
      setSelectedMake('');
      setSelectedRunId('');
      setApifyRunId('');
    }
    setSamplingStrategy(configuredSamplingStrategy);
    setSamplingCount(configuredSamplingCount);
    setForceRerun(configuredForceRerun);
    setConfigureDialogOpen(true);
  };

  const handleSaveConfiguration = () => {
    // Save sampling configuration
    setConfiguredSamplingStrategy(samplingStrategy);
    setConfiguredSamplingCount(samplingCount);

    const configToSave: {
      inputMode: 'inventory_date_make' | 'step1_execution' | 'apify_run_id';
      runId?: string;
      apifyRunId?: string;
      date?: string;
      make?: string;
      samplingStrategy: 'all' | 'random' | 'first' | 'last';
      samplingCount: number;
      forceRerun?: boolean;
    } = {
      inputMode: inputMode,
      samplingStrategy: samplingStrategy,
      samplingCount: samplingCount,
      forceRerun: forceRerun
    };

    if (inputMode === 'step1_execution' && selectedRunId) {
      // Save execution configuration
      setConfiguredRunId(selectedRunId);
      setConfiguredInputMode('step1_execution');
      setConfiguredForceRerun(forceRerun);
      configToSave.runId = selectedRunId;
      // Clear other configurations
      setConfiguredDate(undefined);
      setConfiguredMake('');
      setConfiguredApifyRunId('');
      toast({
        title: 'Configuration Saved',
        description:
          'Step 2 execution has been configured. Click "Process VINs" to start processing.'
      });
      setConfigureDialogOpen(false);
    } else if (inputMode === 'apify_run_id' && apifyRunId) {
      // Save Apify run ID configuration
      setConfiguredApifyRunId(apifyRunId);
      setConfiguredInputMode('apify_run_id');
      setConfiguredForceRerun(forceRerun);
      configToSave.apifyRunId = apifyRunId;
      // Clear other configurations
      setConfiguredRunId('');
      setConfiguredDate(undefined);
      setConfiguredMake('');
      toast({
        title: 'Configuration Saved',
        description:
          'Step 2 Apify run ID has been configured. Click "Process VINs" to start processing.'
      });
      setConfigureDialogOpen(false);
    } else if (inputMode === 'inventory_date_make' && inventoryDate && selectedMake) {
      // Save date/make configuration
      setConfiguredDate(inventoryDate);
      setConfiguredMake(selectedMake);
      setConfiguredInputMode('inventory_date_make');
      setConfiguredForceRerun(forceRerun);
      configToSave.date = inventoryDate.toISOString().split('T')[0];
      configToSave.make = selectedMake;
      // Clear other configurations
      setConfiguredRunId('');
      setConfiguredApifyRunId('');
      toast({
        title: 'Configuration Saved',
        description:
          'Step 2 date/make has been configured. Click "Process VINs" to start processing.'
      });
      setConfigureDialogOpen(false);
    } else {
      toast({
        title: 'Configuration Incomplete',
        description: 'Please select an entry point and provide required information before saving.',
        variant: 'destructive'
      });
      return; // Don't save incomplete config
    }

    // Save configuration to localStorage for persistence across page refreshes
    try {
      localStorage.setItem('vin_processing_config', JSON.stringify(configToSave));
    } catch (error) {
      // Don't block the user if localStorage fails
    }
  };

  const toggleAreaSelection = (area: string) => {
    setSelectedAreas(prev => {
      const newSet = new Set(prev);
      if (newSet.has(area)) {
        newSet.delete(area);
      } else {
        newSet.add(area);
      }
      return newSet;
    });
  };

  const handleRefreshInventory = async () => {
    if (!selectedTenant) {
      toast({
        title: 'Tenant Required',
        description: 'Please select a tenant before executing workflows.',
        variant: 'destructive'
      });
      return;
    }

    if (selectedAreas.size === 0) {
      toast({
        title: 'No Areas Selected',
        description: 'Please select at least one area to refresh.',
        variant: 'destructive'
      });
      return;
    }

    try {
      // Get all tasks for selected areas and OEM
      const tasksToRun: DataCollectionTask[] = [];
      selectedAreas.forEach(area => {
        const areaTasks = getTasksForArea(area);
        tasksToRun.push(...areaTasks);
      });

      if (tasksToRun.length === 0) {
        toast({
          title: 'No Tasks Found',
          description: 'No enabled tasks found for the selected areas.',
          variant: 'destructive'
        });
        return;
      }

      // Filter enabled tasks
      const enabledTasks = tasksToRun.filter(task => task.enabled);

      if (enabledTasks.length === 0) {
        toast({
          title: 'No Enabled Tasks',
          description: `Found ${tasksToRun.length} task(s) but none are enabled. Please enable tasks in the backend.`,
          variant: 'destructive'
        });
        return;
      }

      // Check for any stale "running" statuses in execution history before attempting to run
      const taskNamesToCheck = enabledTasks.map(task => task.name || task.task_id);
      const runningTasks =
        executionHistory?.filter(exec => {
          if (!exec.task_name) return false;
          return (
            isRunningStatus(exec.status) &&
            taskNamesToCheck.some(taskName =>
              exec.task_name?.includes(taskName.split('/').pop() || '')
            )
          );
        }) || [];

      // Log potentially stale running tasks (commented out for production)
      // if (runningTasks.length > 0) {
      // }

      // Run all selected tasks
      // Use Promise.allSettled to handle partial successes (some tasks might already be running)
      const runPromises = enabledTasks.map(task => {
        // Use task.name (e.g., "kenkebiai/cargurus-ford-fl-ft-myers") as task_name
        // The backend expects the actual task name, not the task_id
        const taskName = task.name || task.task_id;

        const request = {
          task_name: taskName,
          tenant_id: selectedTenant, // Include tenant ID for multi-tenant execution
          trigger_mode: 'on_demand' as const,
          wait_for_completion: false,
          force: forceRun // Allow user to force run even if Apify shows active runs
        };
        // Wrap the mutation in a promise that catches errors
        return {
          taskName,
          promise: runTaskMutation.mutateAsync(request).catch(error => {
            // Ensure error is properly caught and re-thrown so Promise.allSettled can handle it
            throw error;
          })
        };
      });

      const results = await Promise.allSettled(runPromises.map(r => r.promise));

      // Process results - separate successes and failures
      const successfulRuns: Array<{ run_id: string; taskName: string }> = [];
      const failedRuns: Array<{ taskName: string; error: any }> = [];

      for (let i = 0; i < results.length; i++) {
        const result = results[i];
        const taskName = runPromises[i]?.taskName || 'Unknown';

        if (result && result.status === 'fulfilled' && 'value' in result) {
          successfulRuns.push({ run_id: result.value.run_id, taskName });
        } else if (result && result.status === 'rejected' && 'reason' in result) {
          failedRuns.push({ taskName, error: result.reason });
        }
      }

      // If all tasks failed, show error and return
      if (successfulRuns.length === 0 && failedRuns.length > 0) {
        const firstError = failedRuns[0]?.error;
        if (!firstError) return;
        let errorMessage = 'Failed to start all data collection tasks';
        let recoverySuggestion = '';

        // Variables for stale run handling (declared outside if blocks for scope)
        let activeRunsDetails: Array<{
          runId: string;
          status: string;
          startedAt: string;
          taskName: string;
          apifyStatus?: string;
          apifyFinishedAt?: string;
          isStale: boolean;
        }> = [];
        let hasStaleRuns = false;
        let taskNames: string[] = [];

        if (firstError instanceof APIError) {
          if (firstError.status === 409) {
            // Enhanced backend response now includes active_runs array with details
            // Format: [{ run_id, status, started_at, apify_status, apify_finished_at, is_stale }]
            const activeRunsFromBackend = firstError.response?.active_runs || [];
            const activeRunIdsFromBackend =
              Array.isArray(activeRunsFromBackend) &&
              activeRunsFromBackend.length > 0 &&
              typeof activeRunsFromBackend[0] === 'string'
                ? activeRunsFromBackend // Legacy format: array of strings
                : activeRunsFromBackend.map((r: any) => r.run_id || r); // Enhanced format: array of objects

            // Check if backend provided enhanced format with is_stale flags
            const hasEnhancedFormat =
              Array.isArray(activeRunsFromBackend) &&
              activeRunsFromBackend.length > 0 &&
              typeof activeRunsFromBackend[0] === 'object' &&
              'is_stale' in activeRunsFromBackend[0];

            // Extract active runs details (use enhanced format if available, otherwise lookup)
            activeRunsDetails = hasEnhancedFormat
              ? activeRunsFromBackend.map((run: any) => ({
                  runId: run.run_id || run.id,
                  status: run.status || 'Unknown',
                  startedAt: run.started_at ? new Date(run.started_at).toLocaleString() : 'Unknown',
                  taskName: run.task_name || 'Unknown',
                  apifyStatus: run.apify_status,
                  apifyFinishedAt: run.apify_finished_at,
                  isStale: run.is_stale || false
                }))
              : activeRunIdsFromBackend.map((runId: string) => {
                  const run = executionHistory?.find(exec => exec.run_id === runId);
                  if (run) {
                    const startedAt = run.started_at
                      ? new Date(run.started_at).toLocaleString()
                      : 'Unknown';
                    return {
                      runId,
                      status: run.status || 'Unknown',
                      startedAt,
                      taskName: run.task_name || 'Unknown',
                      apifyStatus: undefined,
                      apifyFinishedAt: undefined,
                      isStale: false
                    };
                  }
                  return {
                    runId,
                    status: 'Unknown (not found in history)',
                    startedAt: 'Unknown',
                    taskName: 'Unknown',
                    apifyStatus: undefined,
                    apifyFinishedAt: undefined,
                    isStale: false
                  };
                });

            // Check for stale runs
            const staleRuns = activeRunsDetails.filter((r: { isStale: boolean }) => r.isStale);
            hasStaleRuns = staleRuns.length > 0;

            // Extract task name from error message if available
            taskNames = failedRuns
              .filter(f => f.error instanceof APIError && f.error.status === 409)
              .map(f => {
                const errorMsg = f.error.message || '';
                const match = errorMsg.match(/Task\s+([^\s]+)\s+already/);
                if (match) return match[1];
                return f.taskName.split('/').pop() || f.taskName;
              });

            if (taskNames.length === 1) {
              if (activeRunsDetails.length > 0) {
                const runDetails = activeRunsDetails
                  .map(run => {
                    let detail = `Run ID: ${run.runId}, Status: ${run.status}, Started: ${run.startedAt}`;
                    if (run.isStale && run.apifyStatus) {
                      detail += ` (Stale - Apify shows: ${run.apifyStatus})`;
                    }
                    return detail;
                  })
                  .join('\n');

                errorMessage = `Task "${taskNames[0]}" has ${activeRunsDetails.length} active run(s) in the database`;

                if (hasStaleRuns) {
                  recoverySuggestion = `The backend found ${staleRuns.length} stale run(s) that are actually completed in Apify:\n${runDetails}\n\nYou can sync these runs to update their status, or use "Force Run" to override.`;
                } else {
                  recoverySuggestion = `The backend found active run(s):\n${runDetails}\n\nCheck the Execution History tab or use the "Force Run" option to override.`;
                }
              } else {
                errorMessage = `Task "${taskNames[0]}" is already running`;
                recoverySuggestion =
                  'The backend found an active run for this task. Check the Execution History tab or use the "Force Run" option to override.';
              }
            } else {
              errorMessage = `All selected tasks are already running (${taskNames.length} task${taskNames.length > 1 ? 's' : ''})`;
              recoverySuggestion =
                'The backend found active runs for these tasks. Check the Execution History tab or use the "Force Run" option to override.';
            }
          } else if (firstError.status === 400) {
            errorMessage = 'Invalid request. Please check your selections and try again.';
            recoverySuggestion =
              'Verify that you have selected at least one area and that all required fields are filled.';
          } else {
            errorMessage =
              firstError.message ||
              `Failed to start tasks: ${firstError.statusText || 'Unknown error'}`;
            recoverySuggestion = 'Please try again. If the problem continues, contact support.';
          }
        } else if (firstError instanceof Error) {
          errorMessage = firstError.message;
          recoverySuggestion = 'Please try again. If the problem continues, contact support.';
        }

        // Store stale runs for sync functionality
        const staleRunsForSync =
          firstError instanceof APIError &&
          firstError.status === 409 &&
          activeRunsDetails.length > 0
            ? activeRunsDetails.filter(r => r.isStale).map(r => r.runId)
            : [];

        // Handler to sync stale runs
        const handleSyncStaleRuns = async () => {
          if (staleRunsForSync.length === 0) return;

          try {
            // Sync all stale runs
            const syncPromises = staleRunsForSync.map((runId: string) =>
              syncRunStatusMutation.mutateAsync({
                runId,
                request: { sync_from_apify: true }
              })
            );

            await Promise.all(syncPromises);

            toast({
              title: 'Status Synced',
              description: `Successfully synced ${staleRunsForSync.length} stale run(s) from Apify. You can now try refreshing again.`,
              duration: 5000
            });

            // Retry the refresh after a short delay
            setTimeout(() => {
              handleRefreshInventory();
            }, 1000);
          } catch (error) {
            toast({
              title: 'Sync Failed',
              description: `Failed to sync stale runs: ${error instanceof Error ? error.message : 'Unknown error'}`,
              variant: 'destructive',
              duration: 5000
            });
          }
        };

        // Handler to cleanup all stale statuses for the task
        const handleCleanupStaleStatuses = async () => {
          const taskName = taskNames && taskNames.length === 1 ? taskNames[0] : undefined;

          try {
            const request: CleanupStaleStatusesRequest = {
              older_than_hours: 1,
              statuses: ['running', 'queued', 'pending'],
              sync_from_apify: true
            };
            if (taskName) {
              request.task_name = taskName;
            }

            const result = await cleanupStaleStatusesMutation.mutateAsync(request);

            toast({
              title: 'Cleanup Complete',
              description: `Updated ${result.runs_updated} stale run(s). ${result.runs_skipped} run(s) are still active.`,
              duration: 5000
            });

            // Retry the refresh after a short delay
            setTimeout(() => {
              handleRefreshInventory();
            }, 1000);
          } catch (error) {
            toast({
              title: 'Cleanup Failed',
              description: `Failed to cleanup stale statuses: ${error instanceof Error ? error.message : 'Unknown error'}`,
              variant: 'destructive',
              duration: 5000
            });
          }
        };

        // Don't close dialog on error - let user see what went wrong
        toast({
          title: 'Error Starting Refresh',
          description: (
            <div className='space-y-3'>
              <p className='font-medium'>{errorMessage}</p>
              {recoverySuggestion && (
                <div className='text-xs opacity-90 whitespace-pre-line'>{recoverySuggestion}</div>
              )}
              {hasStaleRuns && staleRunsForSync.length > 0 && (
                <div className='flex gap-2 pt-2'>
                  <Button
                    size='sm'
                    variant='outline'
                    onClick={handleSyncStaleRuns}
                    disabled={syncRunStatusMutation.isPending}
                  >
                    {syncRunStatusMutation.isPending ? (
                      <>
                        <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                        Syncing...
                      </>
                    ) : (
                      <>
                        <RefreshCw className='mr-2 h-4 w-4' />
                        Sync Stale Runs ({staleRunsForSync.length})
                      </>
                    )}
                  </Button>
                  <Button
                    size='sm'
                    variant='outline'
                    onClick={handleCleanupStaleStatuses}
                    disabled={cleanupStaleStatusesMutation.isPending}
                  >
                    {cleanupStaleStatusesMutation.isPending ? (
                      <>
                        <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                        Cleaning...
                      </>
                    ) : (
                      <>
                        <Trash2 className='mr-2 h-4 w-4' />
                        Cleanup All Stale
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          ),
          variant: 'destructive',
          duration: 30000 // Show longer when sync options are available
        });

        // Don't close dialog or clear selections on error
        // setRefreshDialogOpen(false);
        // setSelectedAreas(new Set());
        return;
      }

      // If some tasks succeeded, show partial success message
      if (failedRuns.length > 0) {
        const conflictTasks = failedRuns.filter(
          f => f.error instanceof APIError && f.error.status === 409
        );
        const otherFailedTasks = failedRuns.filter(
          f => !(f.error instanceof APIError && f.error.status === 409)
        );

        if (conflictTasks.length > 0) {
          toast({
            title: 'Partial Success',
            description: `Started ${successfulRuns.length} task(s) successfully. ${conflictTasks.length} task(s) are already running and were skipped.`,
            duration: 8000
          });
        }

        if (otherFailedTasks.length > 0) {
          toast({
            title: 'Some Tasks Failed',
            description: `${otherFailedTasks.length} task(s) failed to start. Please check the execution history for details.`,
            variant: 'destructive',
            duration: 8000
          });
        }
      }

      // Track successful run IDs for status polling
      const newRunIds = successfulRuns.map(r => r.run_id);
      setActiveRunIds(prev => {
        const updated = new Set(prev);
        newRunIds.forEach(id => updated.add(id));
        return updated;
      });

      // Immediately invalidate execution history to show new runs
      if (newRunIds.length > 0) {
        queryClient.invalidateQueries({ queryKey: ['dataCollection', 'executionHistory'] });
      }

      if (successfulRuns.length > 0) {
        toast({
          title: 'Inventory Refresh Started',
          description: `Successfully started ${successfulRuns.length} data collection task(s) for ${selectedAreas.size} area(s) (${getOEMLabel(selectedOEM)}). Monitoring status...`
        });
      }

      setRefreshDialogOpen(false);
      setSelectedAreas(new Set());

      // Start polling for completion - check status every 10 seconds
      // Remove from active runs when completed or failed
      const activeRunIdsSet = new Set(newRunIds);
      const checkCompletion = async () => {
        const stillActive: string[] = [];

        for (const runId of Array.from(activeRunIdsSet)) {
          try {
            const status = await dataCollectionAPI.getTaskStatus(runId);
            if (status && isFinishedStatus(status.status)) {
              activeRunIdsSet.delete(runId);
              setActiveRunIds(prev => {
                const updated = new Set(prev);
                updated.delete(runId);
                return updated;
              });

              // Update localStorage - remove completed run
              const stored = localStorage.getItem('workflow_active_runs');
              if (stored) {
                try {
                  const activeRuns: Array<{ runId: string; timestamp: number }> =
                    JSON.parse(stored);
                  const filtered = activeRuns.filter(r => r.runId !== runId);
                  if (filtered.length > 0) {
                    localStorage.setItem('workflow_active_runs', JSON.stringify(filtered));
                  } else {
                    localStorage.removeItem('workflow_active_runs');
                  }
                } catch (error) {
                  // Silently handle localStorage errors
                }
              }

              // Invalidate execution history to refresh the UI
              runTaskMutation.reset();

              // Show completion toast
              if (isCompletedStatus(status.status)) {
                // Try to get records count from execution history (more accurate)
                // Fall back to status.records_processed if execution history not available yet
                let recordsCount = status.records_processed || 0;
                if (executionHistory) {
                  const execEntry = executionHistory.find(e => e.run_id === runId);
                  if (execEntry) {
                    // Use records field (mapped from database) or records_processed as fallback
                    recordsCount = execEntry.records || execEntry.records_processed || recordsCount;
                  }
                }

                toast({
                  title: 'Task Completed',
                  description: `Task ${status.task_name} completed successfully. Processed ${recordsCount.toLocaleString()} records.`
                });
              } else {
                toast({
                  title: 'Task Failed',
                  description: `Task ${status.task_name} failed: ${status.error || 'Unknown error'}`,
                  variant: 'destructive'
                });
              }
            } else {
              stillActive.push(runId);
            }
          } catch (error) {
            // Silently handle status check errors - keep run in active set
            // Don't spam user with errors for background polling
            stillActive.push(runId);
          }
        }

        return stillActive.length === 0; // Return true if all complete
      };

      // Poll every 10 seconds for up to 30 minutes (180 checks)
      // Real-time status updates are handled by React Query refetch interval
      // This polling is for completion detection and cleanup
      let pollCount = 0;
      const maxPolls = 180;
      const pollInterval = setInterval(async () => {
        pollCount++;
        const allComplete = await checkCompletion();

        // Stop polling if all runs are complete or max polls reached
        if (allComplete || pollCount >= maxPolls) {
          clearInterval(pollInterval);
          if (pollCount >= maxPolls) {
            // Silently stop - don't spam user with warnings
          }
        }
      }, 10000); // Poll every 10 seconds
    } catch (error) {
      let errorMessage = 'Failed to start inventory refresh';
      let recoverySuggestion = '';

      if (error instanceof APIError) {
        if (error.status === 409) {
          errorMessage = 'One or more data collection tasks are already running';
          recoverySuggestion =
            'Please wait for the current tasks to complete, or check the execution history to see running tasks.';
        } else if (error.status === 400) {
          errorMessage = 'Invalid request. Please check your selections and try again.';
          recoverySuggestion =
            'Verify that you have selected at least one area and that all required fields are filled.';
        } else if (error.status === 401 || error.status === 403) {
          errorMessage = 'You do not have permission to start data collection tasks.';
          recoverySuggestion =
            'Please contact your administrator if you believe you should have access to this feature.';
        } else if (error.status >= 500) {
          errorMessage = 'Server error occurred while starting the refresh.';
          recoverySuggestion =
            'The server may be temporarily unavailable. Please try again in a few moments. If the problem persists, contact support.';
        } else if (error.status === 0 || error.message.includes('fetch')) {
          errorMessage = 'Unable to connect to the server.';
          recoverySuggestion =
            'Please check your internet connection and try again. If the problem persists, the service may be temporarily unavailable.';
        } else {
          errorMessage =
            error.message || `Failed to start refresh: ${error.statusText || 'Unknown error'}`;
          recoverySuggestion =
            'Please try again. If the problem continues, contact support with the error details.';
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
        if (error.message.includes('network') || error.message.includes('fetch')) {
          recoverySuggestion = 'Please check your internet connection and try again.';
        } else {
          recoverySuggestion = 'Please try again. If the problem continues, contact support.';
        }
      } else {
        errorMessage = 'An unexpected error occurred.';
        recoverySuggestion = 'Please try again. If the problem continues, contact support.';
      }

      toast({
        title: 'Error Starting Refresh',
        description: (
          <div className='space-y-1'>
            <p>{errorMessage}</p>
            {recoverySuggestion && <p className='text-xs opacity-90'>{recoverySuggestion}</p>}
          </div>
        ),
        variant: 'destructive',
        duration: 10000 // Show for 10 seconds to allow reading
      });
    }
  };

  const handleProcessVINs = async () => {
    // Check if API URL is configured
    const apiUrl = import.meta.env.VITE_VIN_PROCESSOR_API_URL;
    if (!apiUrl) {
      toast({
        title: 'Configuration Error',
        description:
          'VIN Processor API URL is not configured. Please set VITE_VIN_PROCESSOR_API_URL in your environment variables.',
        variant: 'destructive'
      });
      return;
    }

    // Validate tenant selection (required for tenant-specific runs)
    if (!selectedTenant) {
      toast({
        title: 'Tenant Required',
        description: 'Please select a tenant before processing VINs.',
        variant: 'destructive'
      });
      return;
    }

    // Get selected tenant info
    const selectedTenantData = tenants?.find(t => t.id === selectedTenant);
    if (!selectedTenantData) {
      toast({
        title: 'Invalid Tenant',
        description: 'Selected tenant not found.',
        variant: 'destructive'
      });
      return;
    }

    // Determine which mode and values to use (prefer configured values)
    const actualMode = configuredInputMode || inputMode;
    const actualSamplingStrategy = configuredSamplingStrategy || samplingStrategy;
    const actualSamplingCount = configuredSamplingCount || samplingCount;
    const actualForceRerun = configuredForceRerun !== undefined ? configuredForceRerun : forceRerun;

    // Debug logging BEFORE building sampling options
    console.log('[VIN Processing] Configuration check:', {
      configuredSamplingStrategy,
      samplingStrategy,
      actualSamplingStrategy,
      configuredSamplingCount,
      samplingCount,
      actualSamplingCount
    });

    // Build sampling options
    // IMPORTANT: Explicitly send { strategy: 'all' } to tell backend to process ALL VINs
    // Some backends may apply a default limit (e.g., 100) if sampling is omitted entirely
    const sampling: VINSamplingOptions | undefined =
      actualSamplingStrategy !== 'all'
        ? {
            strategy: actualSamplingStrategy,
            count: actualSamplingCount
          }
        : {
            strategy: 'all' // Explicitly tell backend to process all VINs (no limit)
          };

    // Debug logging to help identify sampling issues
    if (sampling && sampling.strategy !== 'all') {
      console.error(
        `[VIN Processing] ⚠️ SAMPLING ENABLED: ${sampling.strategy}, count: ${sampling.count}. Only ${sampling.count} VINs will be processed instead of all available VINs.`
      );
    } else if (sampling && sampling.strategy === 'all') {
      console.log('[VIN Processing] ✓ Explicitly requesting ALL VINs (strategy: "all").');
    } else {
      console.log('[VIN Processing] ✓ No sampling - all VINs will be processed.');
    }

    // Build request based on entry point
    // IMPORTANT: Start with minimal request - only add entry point fields based on actualMode
    const request: CreateJobRequest = {
      tenant_id: selectedTenant,
      ...(selectedTenantData.slug && { tenant_slug: selectedTenantData.slug }),
      ...(sampling && { sampling }),
      force_rerun: actualForceRerun,
      metadata: {
        entry_point: actualMode,
        input_params: {},
        requested_by: user?.id || 'unknown',
        requested_at: new Date().toISOString()
      }
    };

    // Initialize all entry point fields as undefined to ensure clean state
    // We'll only set the ones needed for the selected entry point
    request.inventory_date = undefined;
    request.zone_slug = undefined;
    request.make_name = undefined;
    request.run_id = undefined;
    request.apify_run_id = undefined;
    request.vins = undefined;

    // Entry Point 1: Inventory Date + Make
    if (actualMode === 'inventory_date_make') {
      const dateToUse = configuredDate || inventoryDate;
      const makeToUse = configuredMake || selectedMake;

      if (!dateToUse) {
        toast({
          title: 'Date Required',
          description: 'Please select an inventory date.',
          variant: 'destructive'
        });
        return;
      }

      if (!makeToUse) {
        toast({
          title: 'Make Required',
          description: 'Please select a vehicle make.',
          variant: 'destructive'
        });
        return;
      }

      // Format date as YYYY-MM-DD
      const year = dateToUse.getFullYear();
      const month = String(dateToUse.getMonth() + 1).padStart(2, '0');
      const day = String(dateToUse.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;

      // Map frontend make values to backend expected values
      const makeMapping: Record<string, string> = {
        ford: 'Ford',
        hyundai: 'Hyundai',
        stellantis: 'Stellantis'
      };
      const makeName = makeMapping[makeToUse] || makeToUse;

      request.inventory_date = dateString;
      request.make_name = makeName;
      request.metadata!.input_params = {
        inventory_date: dateString,
        make_name: makeName
      };
    }
    // Entry Point 2: Step 1 Execution (run_id)
    else if (actualMode === 'step1_execution') {
      const runIdToUse = configuredRunId || selectedRunId;

      if (!runIdToUse) {
        toast({
          title: 'Execution Required',
          description: 'Please select a Step 1 execution.',
          variant: 'destructive'
        });
        return;
      }

      // Find the selected execution to extract additional context
      const selectedExecution = executionHistory?.find(
        exec => exec.id === runIdToUse || exec.run_id === runIdToUse
      );

      // Store context values for metadata only (NOT as top-level fields when using run_id entry point)
      let contextMakeName: string | undefined;
      let contextZoneSlug: string | undefined;
      let contextInventoryDate: string | undefined;

      if (selectedExecution) {
        // Prefer input_params from execution history (backend now provides this)
        // Check if input_params exists AND has actual values (not just empty object)
        const inputParams = selectedExecution.input_params;
        const hasInputParams =
          inputParams &&
          (inputParams.inventory_date || inputParams.zone_slug || inputParams.make_name);

        if (hasInputParams) {
          // Store values for metadata only (NOT as top-level request fields)
          if (inputParams.inventory_date) {
            contextInventoryDate = inputParams.inventory_date;
          }
          if (inputParams.zone_slug) {
            contextZoneSlug = inputParams.zone_slug;
          }
          if (inputParams.make_name) {
            contextMakeName = inputParams.make_name;
          }
        } else {
          // Fallback: Extract from other fields (for backward compatibility with old executions or empty input_params)

          // Extract make_name from task_name or oem
          const makeName = selectedExecution.oem
            ? selectedExecution.oem.charAt(0).toUpperCase() + selectedExecution.oem.slice(1)
            : getOEMFromTaskName(selectedExecution.task_name || '');

          // Extract zone_slug from zone field
          // If zone is in display format (e.g., "Tampa Bay" or "Ft Myers"), convert to slug format (e.g., "tampa-bay" or "ft-myers")
          let zoneSlug: string | null = null;
          if (selectedExecution.zone) {
            zoneSlug = selectedExecution.zone
              .toLowerCase()
              .replace(/\s+/g, '-') // Replace spaces with hyphens
              .replace(/[^a-z0-9-]/g, ''); // Remove special characters
          }

          // Extract inventory_date from started_at (use the date part)
          let inventoryDate: string | undefined;
          if (selectedExecution.started_at) {
            const date = new Date(selectedExecution.started_at);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            inventoryDate = `${year}-${month}-${day}`;
          }

          // Store extracted values for metadata only
          if (makeName && makeName !== 'Unknown') {
            contextMakeName = makeName;
          } else {
          }
          if (zoneSlug) {
            contextZoneSlug = zoneSlug;
          } else {
          }
          if (inventoryDate) {
            contextInventoryDate = inventoryDate;
          } else {
          }
        }
      }

      // Use exec.id (UUID) if available, otherwise fall back to run_id
      // The backend expects UUID format for run_id according to OpenAPI spec
      // According to the OpenAPI spec, when run_id is provided (Entry Point 2), the backend
      // automatically extracts inventory_date, zone_slug, make_name, and oem from the Step 1
      // execution's input_params. These fields are NOT required in the request.
      const executionId = selectedExecution?.id || runIdToUse;
      request.run_id = executionId;

      // DO NOT include inventory_date, zone_slug, or make_name as top-level fields when using run_id.
      // The backend will extract these from the Step 1 execution's input_params automatically.
      // Including them would violate the oneOf constraint and cause "multiple entry points" error.

      // Include context in metadata.input_params for reference/audit trail only
      request.metadata!.input_params = {
        run_id: executionId,
        // Include context in input_params for reference (NOT as top-level fields)
        ...(contextMakeName && { make_name: contextMakeName }),
        ...(contextZoneSlug && { zone_slug: contextZoneSlug }),
        ...(contextInventoryDate && { inventory_date: contextInventoryDate })
      };
    }
    // Entry Point 3: Apify Run ID (direct)
    else if (actualMode === 'apify_run_id') {
      const apifyRunIdToUse = configuredApifyRunId || apifyRunId;

      if (!apifyRunIdToUse) {
        toast({
          title: 'Apify Run ID Required',
          description: 'Please enter an Apify run ID.',
          variant: 'destructive'
        });
        return;
      }

      request.apify_run_id = apifyRunIdToUse;
      request.metadata!.input_params = {
        apify_run_id: apifyRunIdToUse
      };
    }

    // Log the request being sent to the backend
    console.log('=== VIN Processing Job Request ===');
    console.log('Request Details:', {
      entry_point: actualMode,
      tenant_id: selectedTenant,
      tenant_slug: selectedTenantData.slug,
      force_rerun: actualForceRerun,
      sampling: sampling,
      ...(actualMode === 'inventory_date_make' && {
        inventory_date: request.inventory_date,
        make_name: request.make_name
      }),
      ...(actualMode === 'step1_execution' && {
        run_id: request.run_id
      }),
      ...(actualMode === 'apify_run_id' && {
        apify_run_id: request.apify_run_id
      }),
      metadata: request.metadata
    });
    console.log('Full Request Object:', JSON.stringify(request, null, 2));
    console.log('================================');

    try {
      // Show loading toast immediately
      toast({
        title: 'Starting VIN Processing',
        description: 'Creating job request... Please wait.',
        variant: 'default'
      });

      // Log the final request to debug sampling issues
      console.log('[VIN Processing] Final request being sent:', {
        ...request,
        sampling: request.sampling || 'NOT SET (will process all VINs)',
        hasSampling: !!request.sampling
      });

      const response = await createVINJobMutation.mutateAsync(request);

      // Determine job ID based on dispatch method
      let jobId: string | null = null;
      if (response.dispatch_method === 'direct_http') {
        // Direct HTTP: job is created immediately, use job_id to track status
        jobId = response.job_id || response.request_id || null;
      } else if (response.dispatch_method === 'cloud_tasks') {
        // Cloud Tasks: job is queued, need to poll history to find job_id
      } else {
        // Fallback: try to get job_id or request_id
        jobId = response.job_id || response.request_id || null;
      }

      // Show success toast with appropriate message
      const toastMessage =
        response.dispatch_method === 'direct_http' && jobId
          ? `Job created successfully. Job ID: ${jobId.slice(0, 8)}... The job will appear in Execution History below.`
          : response.dispatch_method === 'cloud_tasks'
            ? 'Job request queued. It will appear in Execution History once processing starts.'
            : 'Job request accepted. Check Execution History for status.';

      toast({
        title: 'VIN Processing Started',
        description: toastMessage,
        variant: 'default'
      });

      // Add job to active jobs set for faster polling (jobId was already extracted above)
      if (jobId) {
        setActiveJobIds(prev => {
          const updated = new Set(prev);
          updated.add(jobId);
          return updated;
        });
      }

      // Immediately invalidate execution history to show new runs
      queryClient.invalidateQueries({ queryKey: ['vinProcessing', 'history'] });
      queryClient.invalidateQueries({ queryKey: ['dataCollection', 'executionHistory'] });

      // Force immediate refetch to show the new job in execution history
      // The backend should create an entry in prod.vin_processing_run_logs immediately when job is created
      await queryClient.refetchQueries({ queryKey: ['vinProcessing', 'history'] });

      // Scroll to execution history section after a short delay
      setTimeout(() => {
        const executionHistorySection = document.querySelector(
          '[data-section="execution-history"]'
        );
        if (executionHistorySection) {
          executionHistorySection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 500);

      setProcessDialogOpen(false);
      // Don't clear configured values - they should persist
      // Only clear dialog state if not configured
      if (!configuredDate) setInventoryDate(undefined);
      if (!configuredMake) setSelectedMake('');
      if (!configuredRunId) setSelectedRunId('');
      if (!configuredApifyRunId) setApifyRunId('');
    } catch (error) {
      console.error('[VIN Processing] Job create failed', error);
      if (error instanceof APIError && error.response !== undefined) {
        console.error('[VIN Processing] API response body', error.response);
      }

      let errorMessage = 'Failed to start VIN processing';
      let errorDescription = 'An unexpected error occurred. Please check the console for details.';

      if (error instanceof APIError) {
        // Extract detailed error message from nested error structure (APIError uses .response)
        let detailedError = error.message || 'API Error';
        const responseBody = error.response;
        if (responseBody && typeof responseBody === 'object') {
          if ('error' in responseBody && typeof responseBody.error === 'string') {
            detailedError = responseBody.error;
          } else if ('message' in responseBody && typeof responseBody.message === 'string') {
            detailedError = responseBody.message;
          } else if ('detail' in responseBody && typeof responseBody.detail === 'string') {
            detailedError = responseBody.detail;
          }
        }

        errorMessage = detailedError;
        errorDescription = `Status: ${error.status} ${error.statusText || ''}.\n\n${detailedError}`;

        // Check for specific validation errors
        if (detailedError.includes('Missing required fields')) {
          errorDescription = `Validation Error: ${detailedError}\n\nPlease ensure you have selected a valid entry point (Step 1 execution, Apify run ID, or inventory date/make).`;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message || 'Unknown error';
        errorDescription = error.message || 'An error occurred while creating the job.';
      } else {
        errorDescription = String(error) || 'An unknown error occurred.';
      }

      // Check if it's a network/connection error
      if (
        error instanceof Error &&
        (error.message.includes('fetch') ||
          error.message.includes('network') ||
          error.message.includes('timeout') ||
          error.message.includes('Failed to fetch') ||
          error.message.includes('AbortError'))
      ) {
        const apiUrl = import.meta.env.VITE_VIN_PROCESSOR_API_URL;
        errorDescription = `Cannot connect to VIN Processor API. Please check:
1. VITE_VIN_PROCESSOR_API_URL is set correctly (current: ${apiUrl ? '✅ Set' : '❌ Missing'})
2. The API service is running in Google Cloud Run
3. Network connectivity is available
4. Check Cloud Run logs for errors`;
      }
      // Check if API URL validation failed
      if (
        error instanceof Error &&
        error.message.includes('VIN Processor API URL not configured')
      ) {
        errorDescription =
          'VITE_VIN_PROCESSOR_API_URL environment variable is not set. Please add it to your .env.local file and restart the dev server.';
      }

      // Still invalidate queries to refresh execution history even on error
      // This ensures we show any jobs that were created before the error
      queryClient.invalidateQueries({ queryKey: ['vinProcessing', 'history'] });

      toast({
        title: errorMessage,
        description: errorDescription,
        variant: 'destructive'
      });

      // Keep dialog open on error so user can retry
      // Don't close the dialog
    }
  };

  // Handle re-running an execution
  const handleRerunExecution = async () => {
    if (!executionToRerun) return;

    const exec = executionToRerun;
    const execId = exec.id || exec.run_id || exec.job_id || '';
    setRerunningExecutionId(execId);
    setRerunConfirmDialogOpen(false);

    try {
      // Validate tenant selection
      if (!selectedTenant) {
        toast({
          title: 'Tenant Required',
          description: 'Please select a tenant before re-running the execution.',
          variant: 'destructive'
        });
        setRerunningExecutionId(null);
        return;
      }

      // Get selected tenant info
      const selectedTenantData = tenants?.find(t => t.id === selectedTenant);
      if (!selectedTenantData) {
        toast({
          title: 'Invalid Tenant',
          description: 'Selected tenant not found.',
          variant: 'destructive'
        });
        setRerunningExecutionId(null);
        return;
      }
      if (exec.executionType === 'data_collection') {
        // Re-run Step 1: Data Collection
        if (!exec.task_name) {
          toast({
            title: 'Invalid Execution',
            description: 'Cannot re-run: task name is missing.',
            variant: 'destructive'
          });
          setRerunningExecutionId(null);
          return;
        }

        // Show loading toast
        toast({
          title: 'Re-running Data Collection',
          description: `Starting task: ${exec.task_name}...`,
          variant: 'default'
        });

        const request = {
          task_name: exec.task_name,
          tenant_id: selectedTenant,
          trigger_mode: 'on_demand' as const,
          wait_for_completion: false,
          force: false
        };

        const response = await runTaskMutation.mutateAsync(request);

        // Show success toast
        toast({
          title: 'Data Collection Started',
          description: `Task "${exec.task_name}" has been re-run. Run ID: ${response.run_id?.slice(0, 8)}... It will appear in Execution History below.`,
          variant: 'default'
        });

        // Add to active runs for polling
        if (response.run_id) {
          setActiveRunIds(prev => {
            const updated = new Set(prev);
            updated.add(response.run_id);
            return updated;
          });
        }

        // Invalidate execution history
        queryClient.invalidateQueries({ queryKey: ['dataCollection', 'executionHistory'] });

        // Scroll to execution history section
        setTimeout(() => {
          const executionHistorySection = document.querySelector(
            '[data-section="execution-history"]'
          );
          if (executionHistorySection) {
            executionHistorySection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 500);
      } else if (exec.executionType === 'vin_processing') {
        // Re-run Step 2: VIN Processing
        const apiUrl = import.meta.env.VITE_VIN_PROCESSOR_API_URL;
        if (!apiUrl) {
          toast({
            title: 'Configuration Error',
            description:
              'VIN Processor API URL is not configured. Please set VITE_VIN_PROCESSOR_API_URL in your environment variables.',
            variant: 'destructive'
          });
          setRerunningExecutionId(null);
          return;
        }

        // Show loading toast
        toast({
          title: 'Re-running VIN Processing',
          description: 'Creating job request... Please wait.',
          variant: 'default'
        });

        // Extract entry point and input parameters from execution
        const entryPoint =
          exec.entry_point || exec['input_params']?.['entry_point'] || 'step1_execution';
        const inputParams = exec['input_params'] || {};

        // Build request based on entry point
        const request: CreateJobRequest = {
          tenant_id: selectedTenant,
          ...(selectedTenantData.slug && { tenant_slug: selectedTenantData.slug }),
          metadata: {
            entry_point: entryPoint,
            input_params: {},
            requested_by: user?.id || 'unknown',
            requested_at: new Date().toISOString()
          }
        };

        // Initialize all entry point fields as undefined
        request.inventory_date = undefined;
        request.zone_slug = undefined;
        request.make_name = undefined;
        request.run_id = undefined;
        request.apify_run_id = undefined;
        request.vins = undefined;

        // Set fields based on entry point
        if (entryPoint === 'inventory_date_make') {
          const inventoryDate = inputParams['inventory_date'] || inputParams['inventoryDate'];
          const makeName = inputParams['make_name'] || inputParams['makeName'];

          if (inventoryDate) request.inventory_date = inventoryDate as string;
          if (makeName) request.make_name = makeName as string;

          request.metadata!.input_params = {
            inventory_date: inventoryDate,
            make_name: makeName
          };
        } else if (entryPoint === 'step1_execution') {
          // Use exec.id (UUID) if available, otherwise fall back to run_id
          const executionId = exec.id || exec.run_id || inputParams['run_id'];
          if (executionId) {
            request.run_id = executionId;
            request.metadata!.input_params = {
              run_id: executionId,
              ...(inputParams['make_name'] && { make_name: inputParams['make_name'] }),
              ...(inputParams['zone_slug'] && { zone_slug: inputParams['zone_slug'] }),
              ...(inputParams['inventory_date'] && {
                inventory_date: inputParams['inventory_date']
              })
            };
          }
        } else if (entryPoint === 'apify_run_id') {
          const apifyRunId = exec.run_id || inputParams['apify_run_id'];
          if (apifyRunId) {
            request.apify_run_id = apifyRunId as string;
            request.metadata!.input_params = {
              apify_run_id: apifyRunId
            };
          }
        }

        const response = await createVINJobMutation.mutateAsync(request);

        // Determine job ID
        let jobId: string | null = null;
        if (response.dispatch_method === 'direct_http') {
          jobId = response.job_id || response.request_id || null;
        } else {
          jobId = response.job_id || response.request_id || null;
        }

        // Show success toast
        const toastMessage =
          response.dispatch_method === 'direct_http' && jobId
            ? `Job created successfully. Job ID: ${jobId.slice(0, 8)}... The job will appear in Execution History below.`
            : response.dispatch_method === 'cloud_tasks'
              ? 'Job request queued. It will appear in Execution History once processing starts.'
              : 'Job request accepted. Check Execution History for status.';

        toast({
          title: 'VIN Processing Started',
          description: toastMessage,
          variant: 'default'
        });

        // Add job to active jobs set
        if (jobId) {
          setActiveJobIds(prev => {
            const updated = new Set(prev);
            updated.add(jobId);
            return updated;
          });
        }

        // Invalidate execution history
        queryClient.invalidateQueries({ queryKey: ['vinProcessing', 'history'] });
        queryClient.invalidateQueries({ queryKey: ['dataCollection', 'executionHistory'] });
        await queryClient.refetchQueries({ queryKey: ['vinProcessing', 'history'] });

        // Scroll to execution history section
        setTimeout(() => {
          const executionHistorySection = document.querySelector(
            '[data-section="execution-history"]'
          );
          if (executionHistorySection) {
            executionHistorySection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 500);
      }
    } catch (error) {
      console.error('[VIN Processing] Re-run job create failed', error);
      if (error instanceof APIError && error.response !== undefined) {
        console.error('[VIN Processing] API response body', error.response);
      }

      const errorMessage = 'Failed to re-run execution';
      let errorDescription = 'An unexpected error occurred. Please check the console for details.';

      if (error instanceof APIError) {
        let detailedError = error.message || 'API Error';
        const responseBody = error.response;
        if (responseBody && typeof responseBody === 'object') {
          if ('error' in responseBody && typeof responseBody.error === 'string') {
            detailedError = responseBody.error;
          } else if ('message' in responseBody && typeof responseBody.message === 'string') {
            detailedError = responseBody.message;
          } else if ('detail' in responseBody && typeof responseBody.detail === 'string') {
            detailedError = responseBody.detail;
          }
        }
        errorDescription = detailedError;
      } else if (error instanceof Error) {
        errorDescription = error.message;
      }

      toast({
        title: errorMessage,
        description: errorDescription,
        variant: 'destructive'
      });
    } finally {
      setRerunningExecutionId(null);
    }
  };

  // Show loading state
  if (tasksLoading || historyLoading) {
    return (
      <div className='flex flex-col items-center justify-center h-64 space-y-4'>
        <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
        <p className='text-sm text-muted-foreground'>
          {tasksLoading && historyLoading
            ? 'Loading tasks and execution history...'
            : tasksLoading
              ? 'Loading tasks...'
              : 'Loading execution history...'}
        </p>
      </div>
    );
  }

  // Show error state
  if (tasksError) {
    return (
      <div className='flex flex-col items-center justify-center h-64 space-y-4'>
        <AlertCircle className='h-12 w-12 text-destructive' />
        <div className='text-center'>
          <h3 className='text-lg font-semibold'>Failed to Load Tasks</h3>
          <p className='text-muted-foreground'>
            {tasksError instanceof Error ? tasksError.message : 'Unknown error occurred'}
          </p>
        </div>
        <Button onClick={() => window.location.reload()}>Retry</Button>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Tenant Selection Section - Compact */}
      <div className='flex items-center gap-4 p-4 bg-muted/30 rounded-lg border'>
        <div className='flex items-center gap-2 min-w-0 flex-1'>
          <Database className='h-4 w-4 text-muted-foreground flex-shrink-0' />
          <Label htmlFor='tenant-selector' className='text-sm font-medium whitespace-nowrap'>
            Tenant:
          </Label>
          {tenantsLoading ? (
            <div className='flex items-center gap-2 text-sm text-muted-foreground'>
              <Loader2 className='h-3 w-3 animate-spin' />
              <span>Loading...</span>
            </div>
          ) : tenantsError ? (
            <div className='text-sm text-destructive'>Failed to load tenants</div>
          ) : (
            <Select value={selectedTenant} onValueChange={setSelectedTenant}>
              <SelectTrigger id='tenant-selector' className='w-full max-w-xs h-8'>
                <SelectValue placeholder='Select tenant...' />
              </SelectTrigger>
              <SelectContent
                className='z-[10000]'
                position='popper'
                sideOffset={4}
                style={{ zIndex: 10000 }}
              >
                {tenants && tenants.length > 0 ? (
                  tenants.map(tenant => (
                    <SelectItem key={tenant.id} value={tenant.id}>
                      <div className='flex items-center gap-2'>
                        <span className='font-medium'>{tenant.name}</span>
                        {tenant.slug && (
                          <span className='text-xs text-muted-foreground'>({tenant.slug})</span>
                        )}
                      </div>
                    </SelectItem>
                  ))
                ) : (
                  <SelectItem value='' disabled>
                    No tenants available
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          )}
        </div>
        {selectedTenant && selectedTenantData && (
          <Badge
            variant='outline'
            className='bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 flex-shrink-0'
          >
            <CheckCircle className='h-3 w-3 mr-1' />
            Ready
          </Badge>
        )}
        {!selectedTenant && (
          <Badge
            variant='outline'
            className='bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 flex-shrink-0'
          >
            <AlertCircle className='h-3 w-3 mr-1' />
            Required
          </Badge>
        )}
      </div>

      <WorkflowHealthDashboard
        executionHistory={executionHistory || []}
        isLoading={historyLoading}
      />

      {/* Live Status Banner */}
      {activeRunIds.size > 0 && (
        <div className='bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 flex items-center justify-between animate-in fade-in slide-in-from-top-4 duration-300'>
          <div className='flex items-center gap-3'>
            <div className='relative flex h-3 w-3'>
              <span className='animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75' />
              <span className='relative inline-flex rounded-full h-3 w-3 bg-blue-500' />
            </div>
            <div>
              <h4 className='font-medium text-blue-900 dark:text-blue-100'>
                Workflows in Progress
              </h4>
              <p className='text-sm text-blue-700 dark:text-blue-300'>
                {activeRunIds.size} task{activeRunIds.size !== 1 ? 's' : ''} currently running.
                Results will update automatically.
              </p>
            </div>
          </div>
          <Button
            variant='outline'
            size='sm'
            className='border-blue-300 hover:bg-blue-100 dark:border-blue-700 dark:hover:bg-blue-800/50'
            onClick={() => {
              // Scroll to execution history section
              const executionHistorySection = document.querySelector(
                '[data-section="execution-history"]'
              );
              if (executionHistorySection) {
                executionHistorySection.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }}
          >
            View Details
          </Button>
        </div>
      )}

      <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
        {/* Step 1: Refresh Car Inventory Card */}
        <WorkflowDefinitionCard
          stepNumber={1}
          title='Refresh Car Inventory'
          description='Collect vehicle inventory data from dealer websites for selected geographic areas.'
          icon={<Database className='h-6 w-6' />}
          status={(() => {
            const latestExec = latestRefreshExecution;
            if (!latestExec) return 'idle';
            if (isCompletedStatus(latestExec.status)) return 'success';
            if (isPartialSuccessStatus(latestExec.status)) return 'partial_success';
            if (isFailedStatus(latestExec.status)) return 'failed';
            if (isCancelledStatus(latestExec.status)) return 'cancelled';
            return 'idle';
          })()}
          activeRuns={activeRunIds.size}
          {...(latestRefreshExecution?.started_at && {
            lastRunDate: new Date(latestRefreshExecution.started_at)
          })}
          stats={[
            {
              label: 'Available Areas',
              value: availableAreas.length,
              icon: <Clock className='h-3 w-3' />
            },
            {
              label: 'Total Tasks',
              value: tasks?.length || 0,
              icon: <Database className='h-3 w-3' />
            }
          ]}
          onConfigure={handleOpenRefreshDialog}
          onRun={handleOpenRefreshDialog}
          isConfigureDisabled={!selectedTenant || tasksAvailableForTenant.length === 0}
          isRunDisabled={!selectedTenant || tasksAvailableForTenant.length === 0}
          runLabel='Refresh Now'
          className='md:col-span-1'
        />

        {/* Step 2: Process VINs Card */}
        <WorkflowDefinitionCard
          stepNumber={2}
          title='Process VINs'
          description='Process collected VINs by inventory date and vehicle make to update vehicle details.'
          icon={<Car className='h-6 w-6' />}
          status={(() => {
            const latestExec = latestVINProcessingExecution;
            if (!latestExec) return 'idle';
            if (isCompletedStatus(latestExec.status)) return 'success';
            if (isPartialSuccessStatus(latestExec.status)) return 'partial_success';
            if (isFailedStatus(latestExec.status)) return 'failed';
            if (isCancelledStatus(latestExec.status)) return 'cancelled';
            if (isRunningStatus(latestExec.status)) return 'running';
            return 'idle';
          })()}
          {...(latestVINProcessingExecution?.started_at && {
            lastRunDate: new Date(latestVINProcessingExecution.started_at)
          })}
          stats={[
            {
              label: 'Entry Point',
              value:
                configuredInputMode === 'step1_execution'
                  ? 'Step 1 Execution'
                  : configuredInputMode === 'apify_run_id'
                    ? 'Apify Run ID'
                    : configuredInputMode === 'inventory_date_make'
                      ? 'Date + Make'
                      : 'Not Configured',
              icon: <Settings className='h-3 w-3' />
            },
            {
              label: 'Configuration',
              value:
                configuredInputMode === 'step1_execution' && configuredRunId
                  ? 'Selected Run'
                  : configuredInputMode === 'apify_run_id' && configuredApifyRunId
                    ? `Apify: ${configuredApifyRunId.substring(0, 8)}...`
                    : configuredInputMode === 'inventory_date_make' && configuredDate
                      ? `${configuredMake} - ${configuredDate.toLocaleDateString()}`
                      : 'None',
              icon: <Car className='h-3 w-3' />
            }
          ]}
          onConfigure={handleOpenConfigureDialog}
          onRun={handleOpenProcessDialog}
          isConfigureDisabled={!selectedTenant}
          isRunDisabled={!selectedTenant || createVINJobMutation.isPending}
          runLabel={createVINJobMutation.isPending ? 'Processing...' : 'Process VINs'}
          className='md:col-span-1'
        />
      </div>

      {/* Execution History Section */}
      <div className='space-y-4' data-section='execution-history'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-4'>
            <h3 className='text-lg font-medium'>Execution History</h3>
            {pollingStatus === 'polling' && (
              <span className='flex items-center gap-2 text-blue-600 dark:text-blue-400 text-sm'>
                <Loader2 className='h-3 w-3 animate-spin' />
                <span className='text-xs'>Checking Apify statuses...</span>
              </span>
            )}
            {lastPoll && pollingStatus === 'idle' && (
              <span className='text-xs text-muted-foreground'>
                Last checked: {new Date(lastPoll).toLocaleTimeString()}
              </span>
            )}
          </div>
          <Button
            variant='outline'
            size='sm'
            onClick={async () => {
              console.log('Manual refresh clicked');
              // Force invalidate all execution history caches
              queryClient.invalidateQueries({ queryKey: ['dataCollection', 'executionHistory'] });
              queryClient.invalidateQueries({ queryKey: ['vinProcessing', 'history'] });

              // Refetch both APIs
              await Promise.all([refetchDataCollection(), refetchVINProcessing()]);

              // Also trigger immediate Apify polling
              pollNow();

              console.log('Manual refresh completed');
            }}
            disabled={historyLoading || vinProcessingHistoryLoading || pollingStatus === 'polling'}
            className='flex items-center gap-2'
          >
            <RefreshCw
              className={`h-4 w-4 ${historyLoading || vinProcessingHistoryLoading || pollingStatus === 'polling' ? 'animate-spin' : ''}`}
            />
            Refresh
          </Button>
        </div>
        {historyLoading || vinProcessingHistoryLoading ? (
          <div className='flex items-center justify-center h-32'>
            <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
          </div>
        ) : historyError || vinProcessingHistoryError ? (
          <div className='flex flex-col items-center gap-2 p-6 border rounded-lg bg-card'>
            <AlertCircle className='h-6 w-6 text-destructive' />
            <div className='text-destructive font-medium'>Error Loading Execution History</div>
            <div className='text-sm text-muted-foreground max-w-md text-center'>
              {(vinProcessingHistoryError as any)?.response?.isCorsError ||
              (vinProcessingHistoryError as any)?.isCorsError ? (
                <div>
                  <p className='mb-2'>
                    CORS Error: The VIN Processor backend service is not configured to allow
                    requests from this origin.
                  </p>
                  <p className='text-xs'>
                    Please contact the backend team to add{' '}
                    <code className='bg-muted px-1 rounded'>http://localhost:3003</code> to the CORS
                    allowed origins list for the VIN Processor service.
                  </p>
                </div>
              ) : (
                <p>
                  {(vinProcessingHistoryError || historyError) instanceof Error
                    ? (vinProcessingHistoryError || historyError)?.message
                    : 'Failed to load execution history. Please try again.'}
                </p>
              )}
            </div>
            <Button
              variant='outline'
              size='sm'
              onClick={() => {
                refetchDataCollection();
                refetchVINProcessing();
              }}
              className='mt-2'
            >
              <RefreshCw className='h-4 w-4 mr-2' />
              Retry
            </Button>
          </div>
        ) : groupedExecutionHistory && groupedExecutionHistory.length > 0 ? (
          <div className='space-y-6'>
            {groupedExecutionHistory.map(dateGroup => {
              const formatDate = (dateStr: string): string => {
                if (dateStr === 'Unknown') return 'Unknown Date';

                // Get today's date in EST
                const todayEST = utcToESTDateString(new Date().toISOString());
                // Get yesterday's date in EST
                const yesterdayDate = new Date();
                yesterdayDate.setDate(yesterdayDate.getDate() - 1);
                const yesterdayEST = utcToESTDateString(yesterdayDate.toISOString());

                if (dateStr === todayEST) {
                  return 'Today';
                } else if (dateStr === yesterdayEST) {
                  return 'Yesterday';
                } else {
                  // Parse the EST date string (YYYY-MM-DD) and format for display
                  const [year, month, day] = dateStr.split('-').map(Number);
                  const date = new Date(year, month - 1, day);
                  return date.toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  });
                }
              };

              const sectionKey = dateGroup.date;
              const isExpanded = expandedSections.has(sectionKey);

              return (
                <div
                  key={dateGroup.date}
                  className='border rounded-lg bg-card overflow-hidden shadow-sm'
                >
                  <Collapsible
                    open={isExpanded}
                    onOpenChange={open => {
                      setExpandedSections(prev => {
                        const updated = new Set(prev);
                        if (open) {
                          updated.add(sectionKey);
                        } else {
                          updated.delete(sectionKey);
                        }
                        return updated;
                      });
                    }}
                  >
                    <CollapsibleTrigger asChild>
                      <Button
                        variant='ghost'
                        className='w-full justify-between px-4 py-3 h-auto hover:bg-muted/50 border-b'
                      >
                        <div className='flex items-center gap-2'>
                          {isExpanded ? (
                            <ChevronDown className='h-4 w-4 text-muted-foreground' />
                          ) : (
                            <ChevronRight className='h-4 w-4 text-muted-foreground' />
                          )}
                          <h4 className='text-sm font-semibold text-foreground'>
                            {formatDate(dateGroup.date)}
                          </h4>
                          <Badge variant='outline' className='text-xs'>
                            {dateGroup.executions.length} execution
                            {dateGroup.executions.length !== 1 ? 's' : ''}
                          </Badge>
                        </div>
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <Table>
                        <TableHeader>
                          <TableRow className='bg-muted/20'>
                            <TableHead className='w-[60px]'>#</TableHead>
                            <TableHead>Workflow Step</TableHead>
                            <TableHead>OEM</TableHead>
                            <TableHead>Zone</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Started</TableHead>
                            <TableHead>Finished</TableHead>
                            <TableHead>Duration</TableHead>
                            <TableHead className='text-right'>Records/VINs</TableHead>
                            <TableHead>Metadata</TableHead>
                            <TableHead className='w-[80px]'>Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {dateGroup.executions.map((exec, index) => {
                            // Validation data is available in exec.validation_status and exec.validation_summary

                            let statusBadge;
                            if (isCompletedStatus(exec.status)) {
                              statusBadge = (
                                <Badge className='bg-green-500/20 text-green-600 dark:text-green-400 border-green-500/30'>
                                  <CheckCircle className='h-3 w-3 mr-1' />
                                  Completed
                                </Badge>
                              );
                            } else if (isPartialSuccessStatus(exec.status)) {
                              statusBadge = (
                                <Badge className='bg-amber-500/20 text-amber-700 dark:text-amber-400 border-amber-500/30'>
                                  <AlertCircle className='h-3 w-3 mr-1' />
                                  Completed with errors
                                </Badge>
                              );
                            } else if (isFailedStatus(exec.status)) {
                              statusBadge = (
                                <Badge className='bg-red-500/20 text-red-600 dark:text-red-400 border-red-500/30'>
                                  <AlertCircle className='h-3 w-3 mr-1' />
                                  Failed
                                </Badge>
                              );
                            } else if (isCancelledStatus(exec.status)) {
                              statusBadge = (
                                <Badge className='bg-slate-500/20 text-slate-600 dark:text-slate-400 border-slate-500/30'>
                                  <AlertCircle className='h-3 w-3 mr-1' />
                                  Cancelled
                                </Badge>
                              );
                            } else if (isRunningStatus(exec.status)) {
                              statusBadge = (
                                <div
                                  className='flex flex-col gap-1'
                                  title='Status is automatically checked every 30 seconds'
                                >
                                  <Badge className='bg-blue-500/20 text-blue-600 dark:text-blue-400 border-blue-500/30 animate-pulse'>
                                    <RefreshCw className='h-3 w-3 mr-1 animate-spin' />
                                    Running
                                  </Badge>
                                  {exec.executionType === 'data_collection' && (
                                    <span className='text-xs text-muted-foreground'>
                                      Apify task running...
                                    </span>
                                  )}
                                </div>
                              );
                            } else {
                              statusBadge = (
                                <Badge variant='outline' className='capitalize'>
                                  {exec.status || 'Unknown'}
                                </Badge>
                              );
                            }

                            const formatDuration = (
                              totalSeconds: number | undefined | null
                            ): string => {
                              if (totalSeconds === undefined || totalSeconds === null) return 'N/A';
                              const minutes = Math.floor(totalSeconds / 60);
                              const remainingSeconds = Math.floor(totalSeconds % 60);
                              if (minutes > 0) {
                                return `${minutes}m ${remainingSeconds}s`;
                              }
                              return `${totalSeconds}s`;
                            };

                            // Calculate success rate for the execution
                            const calculateSuccessRate = (exec: any): number | null => {
                              // Only calculate for completed executions
                              if (
                                !isCompletedStatus(exec.status) &&
                                !isPartialSuccessStatus(exec.status)
                              ) {
                                return null;
                              }

                              if (exec.executionType === 'vin_processing') {
                                // For VIN Processing: success rate = processed_vins / total_vins
                                if (exec.total_vins && exec.total_vins > 0) {
                                  const processed = exec.processed_vins || 0;
                                  return Math.round((processed / exec.total_vins) * 100);
                                }
                                // If no total_vins but has processed_vins, assume 100% if status is completed
                                if (exec.processed_vins && exec.processed_vins > 0) {
                                  return 100;
                                }
                                return null;
                              } else if (exec.executionType === 'data_collection') {
                                // For Data Collection: success based on validation_status
                                const validationStatus = exec.validation_status?.toLowerCase();
                                if (
                                  validationStatus === 'passed' ||
                                  validationStatus === 'success' ||
                                  validationStatus === 'valid'
                                ) {
                                  return 100;
                                } else if (
                                  validationStatus === 'warning' ||
                                  validationStatus === 'warnings'
                                ) {
                                  // Warnings indicate partial success - assume 85%
                                  return 85;
                                } else if (
                                  validationStatus === 'failed' ||
                                  validationStatus === 'error' ||
                                  validationStatus === 'invalid'
                                ) {
                                  return 0;
                                }
                                // If validation status is not available but execution completed, check records
                                if (exec.records_processed && exec.records_processed > 0) {
                                  // Assume success if records were processed
                                  return 100;
                                }
                                return null;
                              }
                              return null;
                            };

                            // Get color class based on success rate
                            const getSuccessColorClass = (successRate: number | null): string => {
                              if (successRate === null) return '';
                              if (successRate >= 90) {
                                return 'bg-green-50/50 dark:bg-green-950/20 border-l-4 border-l-green-500';
                              } else if (successRate >= 70) {
                                return 'bg-yellow-50/50 dark:bg-yellow-950/20 border-l-4 border-l-yellow-500';
                              } else {
                                return 'bg-red-50/50 dark:bg-red-950/20 border-l-4 border-l-red-500';
                              }
                            };

                            const successRate = calculateSuccessRate(exec);
                            const successColorClass = getSuccessColorClass(successRate);

                            const getWorkflowStep = (
                              exec: any
                            ): { step: number; label: string } => {
                              // Check execution type first
                              if (exec.executionType === 'vin_processing') {
                                return { step: 2, label: 'Step 2: Process VINs' };
                              }

                              // For data collection, check task name
                              const taskName = exec.task_name;
                              if (!taskName) return { step: 0, label: 'Unknown' };

                              // Step 1: Data collection tasks (cargurus tasks)
                              if (taskName.toLowerCase().includes('cargurus')) {
                                return { step: 1, label: 'Step 1: Refresh Car Inventory' };
                              }

                              // Default to Step 2 if not cargurus
                              return { step: 2, label: 'Step 2: Process VINs' };
                            };

                            const workflowStep = getWorkflowStep(exec);

                            // Build metadata object with all relevant details
                            const buildMetadata = () => {
                              const metadata: Record<string, unknown> = {};

                              // Run ID / Job ID
                              if (exec.run_id) {
                                metadata['Run ID'] = exec.run_id;
                              }
                              if (exec.job_id) {
                                metadata['Job ID'] = exec.job_id;
                              }

                              // GCS Path
                              if (exec.gcs_path) {
                                metadata['GCS Path'] = exec.gcs_path;
                              }

                              // Errors
                              if (exec.errors) {
                                metadata['Errors'] = exec.errors;
                              }

                              // Step 1 (Data Collection) validation information - moved from Validation column
                              if (exec.executionType === 'data_collection') {
                                if (exec.validation_status) {
                                  metadata['Validation Status'] = exec.validation_status;
                                }
                                if (exec.validation_summary) {
                                  // Parse validation_summary if it's a string
                                  let validationSummary = exec.validation_summary;
                                  if (typeof validationSummary === 'string') {
                                    try {
                                      validationSummary = JSON.parse(validationSummary);
                                    } catch {
                                      // If parsing fails, keep as string
                                      validationSummary = { summary: validationSummary };
                                    }
                                  }
                                  metadata['Validation Summary'] = validationSummary;
                                }
                              }

                              // Input parameters
                              if (exec.input_params && Object.keys(exec.input_params).length > 0) {
                                metadata['Input Parameters'] = exec.input_params;
                              }
                              // Also check for inventory_summary for data collection inputs
                              if (exec.inventory_summary) {
                                metadata['Inventory Summary'] = exec.inventory_summary;
                              }

                              // Step 1 (Data Collection) validation information - moved from Validation column hover
                              if (exec.executionType === 'data_collection') {
                                if (exec.validation_status) {
                                  metadata['Validation Status'] = exec.validation_status;
                                }
                                if (exec.validation_summary) {
                                  // Parse validation_summary if it's a string
                                  let validationSummary = exec.validation_summary;
                                  if (typeof validationSummary === 'string') {
                                    try {
                                      validationSummary = JSON.parse(validationSummary);
                                    } catch {
                                      // If parsing fails, keep as original string
                                    }
                                  }
                                  metadata['Validation Summary'] = validationSummary;
                                }
                              }

                              // Step 2 (VIN Processing) specific metrics
                              if (exec.executionType === 'vin_processing') {
                                // Total recalls found (from vin_recall_details table)
                                if (
                                  exec.total_recalls !== undefined &&
                                  exec.total_recalls !== null
                                ) {
                                  metadata['Total Recalls Found'] =
                                    exec.total_recalls.toLocaleString();
                                }

                                // Recalls with advance notice
                                if (
                                  exec.recalls_with_advance_notice !== undefined &&
                                  exec.recalls_with_advance_notice !== null
                                ) {
                                  metadata['Recalls with Advance Notice'] =
                                    exec.recalls_with_advance_notice.toLocaleString();
                                }

                                // Processed VINs list - handle both array and GCS path
                                if (
                                  exec.processed_vins_list &&
                                  exec.processed_vins_list.length > 0
                                ) {
                                  metadata['Processed VINs List'] = exec.processed_vins_list;
                                } else if (exec.processed_vins_list_gcs) {
                                  metadata['Processed VINs List (GCS Path)'] =
                                    exec.processed_vins_list_gcs;
                                }

                                // Failed VINs list - handle both array and GCS path
                                if (exec.failed_vins_list && exec.failed_vins_list.length > 0) {
                                  metadata['Failed VINs List'] = exec.failed_vins_list;
                                } else if (exec.failed_vins_list_gcs) {
                                  metadata['Failed VINs List (GCS Path)'] =
                                    exec.failed_vins_list_gcs;
                                }

                                // Check output_summary for recall_statistics and vin_lists from logs endpoint
                                if (
                                  exec.output_summary &&
                                  typeof exec.output_summary === 'object'
                                ) {
                                  const outputSummary = exec.output_summary as Record<
                                    string,
                                    unknown
                                  >;
                                  if (outputSummary.recall_statistics) {
                                    metadata['Recall Statistics (from logs)'] =
                                      outputSummary.recall_statistics;
                                  }
                                  if (outputSummary.vin_lists) {
                                    metadata['VIN Lists (from logs)'] = outputSummary.vin_lists;
                                  }
                                }
                              }

                              // Output summary
                              if (
                                exec.output_summary &&
                                Object.keys(exec.output_summary).length > 0
                              ) {
                                metadata['Output Summary'] = exec.output_summary;
                              }

                              // Additional execution metadata
                              if (exec.entry_point) {
                                metadata['Entry Point'] = exec.entry_point;
                              }
                              if (exec.zone) {
                                metadata['Zone'] = exec.zone;
                              }
                              if (exec.tenant_id) {
                                metadata['Tenant ID'] = exec.tenant_id;
                              }
                              if (exec.requested_by) {
                                metadata['Requested By'] = exec.requested_by;
                              }

                              return metadata;
                            };

                            const metadata = buildMetadata();
                            const hasMetadata = Object.keys(metadata).length > 0;

                            // Get serial number for this execution
                            const execId =
                              exec.id || exec.run_id || exec.job_id || exec.started_at || '';
                            const serialNumber = execId ? executionSerialMap.get(execId) : null;

                            return (
                              <TableRow
                                key={`${dateGroup.date}-${index}-${exec.id || exec.run_id || exec.job_id || exec.started_at}`}
                                className={successColorClass}
                              >
                                <TableCell className='text-center font-mono text-sm text-muted-foreground'>
                                  {serialNumber || '-'}
                                </TableCell>
                                <TableCell>
                                  <Badge
                                    variant={workflowStep.step === 1 ? 'default' : 'secondary'}
                                    className={
                                      workflowStep.step === 1
                                        ? 'bg-blue-500/20 text-blue-600 dark:text-blue-400 border-blue-500/30'
                                        : 'bg-purple-500/20 text-purple-600 dark:text-purple-400 border-purple-500/30'
                                    }
                                  >
                                    {workflowStep.label}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  {exec.executionType === 'vin_processing' ? (
                                    <span className='text-sm text-muted-foreground'>
                                      {exec.input_params?.make_name
                                        ? exec.input_params.make_name.charAt(0).toUpperCase() +
                                          exec.input_params.make_name.slice(1)
                                        : '-'}
                                    </span>
                                  ) : (
                                    <span className='text-sm font-medium'>
                                      {exec.oem
                                        ? exec.oem.charAt(0).toUpperCase() + exec.oem.slice(1)
                                        : 'Unknown'}
                                    </span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <span className='text-sm text-muted-foreground'>
                                    {(() => {
                                      // Try to get zone_slug from input_params first (from metadata)
                                      const inputParams = exec['input_params'];
                                      const zoneSlug =
                                        inputParams &&
                                        typeof inputParams === 'object' &&
                                        'zone_slug' in inputParams
                                          ? inputParams['zone_slug']
                                          : null;

                                      // Fall back to exec.zone if zone_slug not found
                                      return zoneSlug || exec.zone || '-';
                                    })()}
                                  </span>
                                </TableCell>
                                <TableCell>
                                  <div className='flex items-center gap-2'>
                                    {statusBadge}
                                    {/* Show refresh button for stuck VIN processing jobs */}
                                    {exec.executionType === 'vin_processing' &&
                                      isRunningStatus(exec.status) &&
                                      exec.job_id &&
                                      exec.started_at && (
                                        <Button
                                          variant='ghost'
                                          size='sm'
                                          className='h-6 w-6 p-0'
                                          onClick={e => {
                                            e.stopPropagation();
                                            handleRefreshJobStatus(exec.job_id);
                                          }}
                                          disabled={refreshingJobId === exec.job_id}
                                          title='Refresh Cloud Run status'
                                        >
                                          {refreshingJobId === exec.job_id ? (
                                            <Loader2 className='h-3 w-3 animate-spin' />
                                          ) : (
                                            <RefreshCw className='h-3 w-3' />
                                          )}
                                        </Button>
                                      )}
                                    {/* Show refresh button for data collection tasks */}
                                    {exec.executionType === 'data_collection' && exec.run_id && (
                                      <Button
                                        variant='ghost'
                                        size='sm'
                                        className='h-6 w-6 p-0'
                                        onClick={async e => {
                                          e.stopPropagation();
                                          const success = await syncSpecificRun(exec.run_id!);
                                          if (success) {
                                            refetchDataCollection();
                                          }
                                        }}
                                        disabled={syncingRunId}
                                        title='Refresh status'
                                      >
                                        {syncingRunId ? (
                                          <Loader2 className='h-3 w-3 animate-spin' />
                                        ) : (
                                          <RefreshCw className='h-3 w-3' />
                                        )}
                                      </Button>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell className='text-sm text-muted-foreground'>
                                  {exec.started_at
                                    ? new Date(exec.started_at).toLocaleString()
                                    : 'N/A'}
                                </TableCell>
                                <TableCell className='text-sm text-muted-foreground'>
                                  {exec.finished_at
                                    ? new Date(exec.finished_at).toLocaleString()
                                    : isRunningStatus(exec.status)
                                      ? 'Running...'
                                      : '-'}
                                </TableCell>
                                <TableCell className='text-sm font-mono'>
                                  {isRunningStatus(exec.status)
                                    ? 'Running'
                                    : formatDuration(exec.duration_seconds)}
                                </TableCell>
                                <TableCell className='text-right font-mono text-sm'>
                                  {exec.executionType === 'vin_processing'
                                    ? exec.processed_vins !== undefined &&
                                      exec.processed_vins !== null
                                      ? `${exec.processed_vins.toLocaleString()} / ${exec.total_vins?.toLocaleString() || '?'}`
                                      : '-'
                                    : workflowStep.step === 1
                                      ? (() => {
                                          // For Step 1: Refresh Car Inventory, show filtered_count/original_count from metadata
                                          // Helper to safely get nested values from any object structure
                                          const getValue = (obj: any, keys: string[]): any => {
                                            if (!obj || typeof obj !== 'object') return null;
                                            let current = obj;
                                            for (const key of keys) {
                                              if (current && typeof current === 'object') {
                                                // Try both snake_case and camelCase
                                                const snakeKey = key;
                                                const camelKey = key.replace(
                                                  /_([a-z])/g,
                                                  (_, letter) => letter.toUpperCase()
                                                );
                                                current = current[snakeKey] ?? current[camelKey];
                                                if (current === undefined || current === null)
                                                  return null;
                                              } else {
                                                return null;
                                              }
                                            }
                                            return current;
                                          };

                                          // Parse all potential JSON string fields
                                          const parseIfString = (value: any): any => {
                                            if (typeof value === 'string') {
                                              try {
                                                return JSON.parse(value);
                                              } catch {
                                                return value;
                                              }
                                            }
                                            return value;
                                          };

                                          const inventorySummary = parseIfString(
                                            exec.inventory_summary
                                          );
                                          const outputSummary = parseIfString(exec.output_summary);
                                          const validationSummary = parseIfString(
                                            exec.validation_summary
                                          );

                                          // Check all possible locations and nested structures
                                          const filteredCount =
                                            getValue(inventorySummary, ['filtered_count']) ||
                                            getValue(outputSummary, ['filtered_count']) ||
                                            getValue(validationSummary, ['filtered_count']) ||
                                            getValue(exec, ['metadata', 'filtered_count']) ||
                                            getValue(exec, ['input_params', 'filtered_count']) ||
                                            (inventorySummary &&
                                            typeof inventorySummary === 'object' &&
                                            ('filtered_count' in inventorySummary ||
                                              'filteredCount' in inventorySummary)
                                              ? (inventorySummary.filtered_count ??
                                                inventorySummary.filteredCount)
                                              : null) ||
                                            (outputSummary &&
                                            typeof outputSummary === 'object' &&
                                            ('filtered_count' in outputSummary ||
                                              'filteredCount' in outputSummary)
                                              ? (outputSummary.filtered_count ??
                                                outputSummary.filteredCount)
                                              : null) ||
                                            (validationSummary &&
                                            typeof validationSummary === 'object' &&
                                            ('filtered_count' in validationSummary ||
                                              'filteredCount' in validationSummary)
                                              ? (validationSummary.filtered_count ??
                                                validationSummary.filteredCount)
                                              : null);

                                          const originalCount =
                                            getValue(inventorySummary, ['original_count']) ||
                                            getValue(outputSummary, ['original_count']) ||
                                            getValue(validationSummary, ['original_count']) ||
                                            getValue(exec, ['metadata', 'original_count']) ||
                                            getValue(exec, ['input_params', 'original_count']) ||
                                            (inventorySummary &&
                                            typeof inventorySummary === 'object' &&
                                            ('original_count' in inventorySummary ||
                                              'originalCount' in inventorySummary)
                                              ? (inventorySummary.original_count ??
                                                inventorySummary.originalCount)
                                              : null) ||
                                            (outputSummary &&
                                            typeof outputSummary === 'object' &&
                                            ('original_count' in outputSummary ||
                                              'originalCount' in outputSummary)
                                              ? (outputSummary.original_count ??
                                                outputSummary.originalCount)
                                              : null) ||
                                            (validationSummary &&
                                            typeof validationSummary === 'object' &&
                                            ('original_count' in validationSummary ||
                                              'originalCount' in validationSummary)
                                              ? (validationSummary.original_count ??
                                                validationSummary.originalCount)
                                              : null);

                                          if (
                                            filteredCount !== null &&
                                            originalCount !== null &&
                                            filteredCount !== undefined &&
                                            originalCount !== undefined
                                          ) {
                                            const filtered =
                                              typeof filteredCount === 'number'
                                                ? filteredCount.toLocaleString()
                                                : String(filteredCount);
                                            const original =
                                              typeof originalCount === 'number'
                                                ? originalCount.toLocaleString()
                                                : String(originalCount);
                                            return `${filtered} / ${original}`;
                                          }

                                          // Fallback to records_processed if filtered_count/original_count not available
                                          return exec.records_processed?.toLocaleString() || '-';
                                        })()
                                      : exec.records_processed?.toLocaleString() || '-'}
                                </TableCell>
                                <TableCell>
                                  {hasMetadata ? (
                                    <Popover>
                                      <PopoverTrigger asChild>
                                        <Button
                                          variant='ghost'
                                          size='sm'
                                          className='h-8 text-xs'
                                          onClick={e => e.stopPropagation()}
                                        >
                                          <Database className='h-3 w-3 mr-1' />
                                          View Metadata
                                        </Button>
                                      </PopoverTrigger>
                                      <PopoverContent
                                        className='w-[500px] max-h-[600px] overflow-y-auto'
                                        align='start'
                                        onClick={e => e.stopPropagation()}
                                      >
                                        <div className='space-y-4'>
                                          <div className='space-y-1'>
                                            <div className='font-semibold text-sm flex items-center gap-2'>
                                              <Database className='h-4 w-4' />
                                              Execution Metadata
                                            </div>
                                            <div className='text-xs text-muted-foreground'>
                                              All metadata for investigation
                                            </div>
                                          </div>
                                          <div className='space-y-3'>
                                            {Object.entries(metadata)
                                              .filter(
                                                ([_, value]) =>
                                                  value !== null && value !== undefined
                                              )
                                              .map(([key, value]) => {
                                                let formattedValue: string;
                                                const isStringValue = typeof value === 'string';

                                                if (
                                                  typeof value === 'object' &&
                                                  value !== null &&
                                                  !Array.isArray(value)
                                                ) {
                                                  formattedValue = JSON.stringify(value, null, 2);
                                                } else if (Array.isArray(value)) {
                                                  formattedValue = JSON.stringify(value, null, 2);
                                                } else if (typeof value === 'boolean') {
                                                  formattedValue = value ? 'Yes' : 'No';
                                                } else {
                                                  formattedValue = String(value);
                                                }
                                                return (
                                                  <div
                                                    key={key}
                                                    className='border-b border-border/50 pb-3 last:border-0 last:pb-0'
                                                  >
                                                    <div className='font-medium text-sm text-foreground mb-1.5'>
                                                      {key}
                                                    </div>
                                                    <div
                                                      className={`break-words whitespace-pre-wrap ${
                                                        isStringValue && value.length > 100
                                                          ? 'font-mono text-[11px]'
                                                          : 'text-xs'
                                                      } text-muted-foreground bg-muted/50 p-2 rounded max-h-[200px] overflow-y-auto`}
                                                    >
                                                      {formattedValue}
                                                    </div>
                                                  </div>
                                                );
                                              })}
                                          </div>
                                        </div>
                                      </PopoverContent>
                                    </Popover>
                                  ) : (
                                    <span className='text-xs text-muted-foreground'>-</span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {(() => {
                                    const execId = exec.id || exec.run_id || exec.job_id || '';
                                    const isRerunning = rerunningExecutionId === execId;
                                    return (
                                      <Button
                                        variant='ghost'
                                        size='sm'
                                        className='h-8 w-8 p-0'
                                        onClick={e => {
                                          e.stopPropagation();
                                          setExecutionToRerun(exec);
                                          setRerunConfirmDialogOpen(true);
                                        }}
                                        disabled={
                                          isRerunning ||
                                          runTaskMutation.isPending ||
                                          createVINJobMutation.isPending
                                        }
                                        title='Re-run this execution'
                                      >
                                        {isRerunning ? (
                                          <Loader2 className='h-4 w-4 animate-spin' />
                                        ) : (
                                          <RotateCw className='h-4 w-4' />
                                        )}
                                      </Button>
                                    );
                                  })()}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </CollapsibleContent>
                  </Collapsible>
                </div>
              );
            })}

            {/* Show more button */}
            {unifiedExecutionHistory && unifiedExecutionHistory.length > visibleExecutions && (
              <div className='flex justify-center pt-4'>
                <Button
                  variant='outline'
                  onClick={() => setVisibleExecutions(prev => prev + 10)}
                  className='flex items-center gap-2'
                >
                  <RefreshCw className='h-4 w-4' />
                  Show More ({unifiedExecutionHistory.length - visibleExecutions} more)
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className='text-center py-8 text-muted-foreground'>
            <AlertCircle className='h-8 w-8 mx-auto mb-2' />
            <p>No execution history available.</p>
          </div>
        )}
      </div>

      {/* Refresh Inventory Dialog */}
      <Dialog open={refreshDialogOpen} onOpenChange={setRefreshDialogOpen}>
        <DialogContent className='max-w-2xl'>
          <DialogHeader>
            <DialogTitle>Step 1: Refresh Car Inventory</DialogTitle>
            <DialogDescription>
              Select the vehicle make and geographic areas you want to refresh. Each area
              corresponds to a data collection task.
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-4 py-4'>
            {/* Selected Tenant Display (Read-only) */}
            {selectedTenant && selectedTenantData ? (
              <div className='space-y-2'>
                <Label>Selected Tenant</Label>
                <div className='flex items-center gap-2 p-3 bg-muted/50 rounded-lg border'>
                  <CheckCircle className='h-4 w-4 text-green-600 dark:text-green-400' />
                  <div className='flex-1'>
                    <div className='text-sm font-medium'>{selectedTenantData.name}</div>
                    {selectedTenantData.slug && (
                      <div className='text-xs text-muted-foreground'>
                        Slug: {selectedTenantData.slug}
                      </div>
                    )}
                  </div>
                  <Badge
                    variant='outline'
                    className='bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400'
                  >
                    Active
                  </Badge>
                </div>
                <p className='text-xs text-muted-foreground'>
                  All workflow operations will be performed for this tenant. To change the tenant,
                  select a different one at the top of the page.
                </p>
              </div>
            ) : (
              <div className='flex items-center gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800'>
                <AlertCircle className='h-4 w-4 text-amber-600 dark:text-amber-400' />
                <div className='text-sm text-amber-800 dark:text-amber-200'>
                  No tenant selected. Please select a tenant at the top of the page first.
                </div>
              </div>
            )}

            <div className='space-y-2'>
              <Label htmlFor='oem-select-refresh'>Vehicle Make *</Label>
              <Select value={selectedOEM} onValueChange={setSelectedOEM} disabled={!selectedTenant}>
                <SelectTrigger id='oem-select-refresh'>
                  <SelectValue placeholder='Select vehicle make' />
                </SelectTrigger>
                <SelectContent
                  className='z-[10000]'
                  position='popper'
                  sideOffset={4}
                  style={{ zIndex: 10000 }}
                >
                  {makeOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className='text-xs text-muted-foreground'>
                Select the vehicle make to refresh inventory for. Tenant must be selected first.
              </p>
            </div>

            {!selectedTenant ? (
              <div className='text-center py-8 text-muted-foreground'>
                <AlertCircle className='h-8 w-8 mx-auto mb-2' />
                <p>Please select a tenant first to view available tasks and areas.</p>
              </div>
            ) : tasksLoading ? (
              <div className='text-center py-8 text-muted-foreground'>
                <Loader2 className='h-8 w-8 mx-auto mb-2 animate-spin' />
                <p>Loading tasks for selected tenant...</p>
              </div>
            ) : tasksError ? (
              <div className='text-center py-8 text-destructive'>
                <AlertCircle className='h-8 w-8 mx-auto mb-2' />
                <p>
                  Failed to load tasks.{' '}
                  {tasksError instanceof Error ? tasksError.message : 'Unknown error'}
                </p>
              </div>
            ) : availableAreas.length === 0 ? (
              <div className='text-center py-8 text-muted-foreground'>
                <AlertCircle className='h-8 w-8 mx-auto mb-2' />
                <p>
                  No areas available for {getOEMLabel(selectedOEM)}. Please check your data
                  collection tasks for this tenant.
                </p>
              </div>
            ) : (
              <div className='space-y-2'>
                <Label>Select Areas ({getOEMLabel(selectedOEM)})</Label>
                <div className='space-y-3 max-h-[400px] overflow-y-auto border rounded-lg p-3'>
                  {availableAreas.map(areaId => {
                    const areaTasks = getTasksForArea(areaId);
                    const enabledTasks = areaTasks.filter(t => t.enabled);
                    const disabledTasks = areaTasks.filter(t => !t.enabled);
                    const isSelected = selectedAreas.has(areaId);
                    const hasEnabledTasks = enabledTasks.length > 0;

                    // Display name: use task name if available, otherwise use task_id
                    const displayName = areaTasks[0]?.name || areaId;
                    // Clean up the display name (remove common prefixes and OEM names)
                    // Process in order: remove prefix, remove OEM names (with dashes), then format
                    let cleanDisplayName = displayName
                      .toLowerCase()
                      .replace(/^kenkebiai\/cargurus-/, '') // Remove prefix
                      .replace(/-(stellantis|hyundai|ford|lincoln)-/g, '-') // Remove OEM in middle (with dashes)
                      .replace(/^(stellantis|hyundai|ford|lincoln)-/i, '') // Remove OEM at start
                      .replace(/-(stellantis|hyundai|ford|lincoln)$/i, '') // Remove OEM at end
                      .replace(/\b(stellantis|hyundai|ford|lincoln)\b/gi, '') // Remove any remaining OEM words
                      .replace(/-/g, ' ') // Replace dashes with spaces
                      .replace(/\s+/g, ' ') // Remove extra spaces
                      .trim();

                    // Capitalize words
                    cleanDisplayName = cleanDisplayName
                      .split(' ')
                      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                      .join(' ');

                    return (
                      <div
                        key={areaId}
                        className={`flex items-start space-x-3 p-3 border rounded-lg cursor-pointer ${
                          hasEnabledTasks
                            ? 'hover:bg-accent/50'
                            : 'opacity-60 cursor-not-allowed bg-muted/30'
                        }`}
                        onClick={e => {
                          // Don't handle clicks on the checkbox itself - let it handle its own clicks
                          const target = e.target as HTMLElement;
                          if (target.closest('[role="checkbox"]') || target.closest('button')) {
                            return;
                          }
                          if (hasEnabledTasks) {
                            toggleAreaSelection(areaId);
                          }
                        }}
                      >
                        <div onClick={e => e.stopPropagation()}>
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => {
                              if (hasEnabledTasks) {
                                toggleAreaSelection(areaId);
                              }
                            }}
                            disabled={!hasEnabledTasks}
                            className='mt-1'
                          />
                        </div>
                        <div className='flex-1'>
                          <div className='flex items-center justify-between'>
                            <Label
                              className={`font-medium ${hasEnabledTasks ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                            >
                              {cleanDisplayName}
                            </Label>
                            <div className='flex items-center gap-2'>
                              {hasEnabledTasks ? (
                                <Badge
                                  variant='outline'
                                  className='text-xs bg-green-50 text-green-700 border-green-200'
                                >
                                  {enabledTasks.length} enabled
                                </Badge>
                              ) : (
                                <Badge
                                  variant='outline'
                                  className='text-xs bg-gray-50 text-gray-500 border-gray-200'
                                >
                                  Disabled
                                </Badge>
                              )}
                              {disabledTasks.length > 0 && (
                                <Badge variant='outline' className='text-xs text-muted-foreground'>
                                  {disabledTasks.length} disabled
                                </Badge>
                              )}
                            </div>
                          </div>
                          {areaTasks.length > 0 && areaTasks[0]?.zone && (
                            <p className='text-xs text-muted-foreground mt-1'>
                              Zone: {areaTasks[0].zone}
                            </p>
                          )}
                          {!hasEnabledTasks && (
                            <p className='text-xs text-amber-600 dark:text-amber-400 mt-1'>
                              This area has no enabled tasks and cannot be selected.
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Force Run Option */}
            <div className='space-y-2 pt-2 border-t'>
              <div className='flex items-start space-x-2'>
                <Checkbox
                  id='force-run-checkbox'
                  checked={forceRun}
                  onCheckedChange={checked => setForceRun(checked === true)}
                />
                <div className='flex-1'>
                  <Label
                    htmlFor='force-run-checkbox'
                    className='text-sm font-medium cursor-pointer'
                  >
                    Force Run (Override Apify Check)
                  </Label>
                  <p className='text-xs text-muted-foreground mt-1'>
                    If checked, the system will attempt to start the task even if Apify shows an
                    active run. Use this only if you're certain the Apify run is stuck or stale.
                    This may cause duplicate runs.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => setRefreshDialogOpen(false)}
              disabled={runTaskMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRefreshInventory}
              disabled={runTaskMutation.isPending || !selectedTenant || selectedAreas.size === 0}
            >
              {runTaskMutation.isPending ? (
                <>
                  <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                  Starting...
                </>
              ) : (
                <>
                  <Play className='h-4 w-4 mr-2' />
                  Start Refresh ({selectedAreas.size} area{selectedAreas.size !== 1 ? 's' : ''})
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Configure Step 2 Dialog */}
      <Dialog open={configureDialogOpen} onOpenChange={setConfigureDialogOpen}>
        <DialogContent className='max-w-2xl overflow-visible'>
          <DialogHeader>
            <DialogTitle>Configure Step 2: Process VINs</DialogTitle>
            <DialogDescription>
              Choose how to select VINs for processing: by inventory date/make, Step 1 execution, or
              Apify run ID. Your selection will be saved and used when you click "Process VINs".
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-4 py-4'>
            {/* Input Mode Selection */}
            <div className='space-y-2'>
              <Label htmlFor='configure-input-mode'>Entry Point *</Label>
              <Select
                value={inputMode}
                onValueChange={(
                  value: 'inventory_date_make' | 'step1_execution' | 'apify_run_id'
                ) => {
                  setInputMode(value);
                  // Clear other mode's selections when switching
                  if (value === 'inventory_date_make') {
                    setSelectedRunId('');
                    setConfiguredRunId('');
                    setApifyRunId('');
                    setConfiguredApifyRunId('');
                  } else if (value === 'step1_execution') {
                    setInventoryDate(undefined);
                    setSelectedMake('');
                    setApifyRunId('');
                    setConfiguredApifyRunId('');
                  } else {
                    setInventoryDate(undefined);
                    setSelectedMake('');
                    setSelectedRunId('');
                    setConfiguredRunId('');
                  }
                }}
              >
                <SelectTrigger id='configure-input-mode'>
                  <SelectValue placeholder='Select entry point' />
                </SelectTrigger>
                <SelectContent
                  className='z-[10000]'
                  position='popper'
                  sideOffset={4}
                  style={{ zIndex: 10000 }}
                >
                  <SelectItem value='inventory_date_make'>1. Inventory Date + Make</SelectItem>
                  <SelectItem value='step1_execution'>2. Step 1 Execution</SelectItem>
                  <SelectItem value='apify_run_id'>3. Apify Run ID</SelectItem>
                </SelectContent>
              </Select>
              <p className='text-xs text-muted-foreground'>
                {inputMode === 'inventory_date_make'
                  ? 'Process all VINs collected on a specific date for a specific make.'
                  : inputMode === 'step1_execution'
                    ? 'Process VINs from a specific Step 1 data collection execution.'
                    : 'Process VINs from a specific Apify run ID (direct).'}
              </p>
            </div>

            {/* Entry Point 1: Inventory Date + Make */}
            {inputMode === 'inventory_date_make' && (
              <>
                <div className='space-y-2'>
                  <Label htmlFor='configure-inventory-date'>Inventory Date *</Label>
                  <DatePicker
                    value={inventoryDate}
                    onChange={setInventoryDate}
                    placeholder='Select inventory date'
                    fromDate={new Date(2020, 0, 1)}
                    toDate={new Date()}
                  />
                  <p className='text-xs text-muted-foreground'>
                    Select the date when the inventory data was collected.
                  </p>
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='configure-make-select'>Vehicle Make *</Label>
                  <Select value={selectedMake} onValueChange={setSelectedMake}>
                    <SelectTrigger id='configure-make-select'>
                      <SelectValue placeholder='Select vehicle make' />
                    </SelectTrigger>
                    <SelectContent
                      className='z-[10000]'
                      position='popper'
                      sideOffset={4}
                      style={{ zIndex: 10000 }}
                    >
                      {makeOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className='text-xs text-muted-foreground'>
                    Select the vehicle make to process (Ford/Lincoln, Hyundai, or Stellantis).
                  </p>
                </div>
              </>
            )}

            {/* Execution Mode */}
            {inputMode === 'step1_execution' && (
              <div className='space-y-2'>
                <Label htmlFor='configure-execution-select'>Step 1 Execution *</Label>
                {completedStep1Executions.length === 0 ? (
                  <div className='text-sm text-muted-foreground p-4 border rounded-lg bg-muted/50'>
                    <AlertCircle className='h-4 w-4 inline mr-2' />
                    No completed Step 1 executions found. Please run Step 1 first.
                  </div>
                ) : (
                  <Select value={selectedRunId || configuredRunId} onValueChange={setSelectedRunId}>
                    <SelectTrigger id='configure-execution-select'>
                      <SelectValue placeholder='Select a Step 1 execution' />
                    </SelectTrigger>
                    <SelectContent
                      className='z-[10000]'
                      position='popper'
                      sideOffset={4}
                      style={{ zIndex: 10000 }}
                    >
                      {completedStep1Executions.map((exec, index) => {
                        const execDate = exec.started_at ? new Date(exec.started_at) : new Date();
                        const oem = exec.oem || getOEMFromTaskName(exec.task_name || '');
                        const records =
                          exec.records_processed ||
                          exec.inventory_summary?.snapshot_inserted ||
                          exec.inventory_summary?.staging_inserted ||
                          0;
                        // Clean task name for display
                        let displayName = exec.task_name || 'Unknown Task';
                        displayName = displayName
                          .toLowerCase()
                          .replace(/^kenkebiai\/cargurus-/, '')
                          .replace(/-(stellantis|hyundai|ford|lincoln)-/g, '-')
                          .replace(/^(stellantis|hyundai|ford|lincoln)-/i, '')
                          .replace(/-(stellantis|hyundai|ford|lincoln)$/i, '')
                          .replace(/\b(stellantis|hyundai|ford|lincoln)\b/gi, '')
                          .replace(/-/g, ' ')
                          .replace(/\s+/g, ' ')
                          .trim()
                          .split(' ')
                          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                          .join(' ');

                        // Use exec.id (UUID) for value, not exec.run_id (Apify run ID)
                        // The backend expects UUID format for run_id according to OpenAPI spec
                        // Ensure we have a unique identifier - use id, run_id, or fallback to index
                        const executionId = exec.id || exec.run_id || `exec-${index}`;
                        // Create a truly unique key by combining executionId with index as fallback
                        const uniqueKey = executionId || `exec-${index}`;

                        return (
                          <SelectItem key={uniqueKey} value={executionId}>
                            <div className='flex flex-col'>
                              <span className='font-medium'>{displayName}</span>
                              <span className='text-xs text-muted-foreground'>
                                {execDate.toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}{' '}
                                • {oem} • {records.toLocaleString()} records
                              </span>
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                )}
                <p className='text-xs text-muted-foreground'>
                  Select a completed Step 1 execution to process its collected VINs.
                </p>
              </div>
            )}

            {/* Entry Point 3: Apify Run ID */}
            {inputMode === 'apify_run_id' && (
              <div className='space-y-2'>
                <Label htmlFor='configure-apify-run-id'>Apify Run ID *</Label>
                <Input
                  id='configure-apify-run-id'
                  value={apifyRunId || configuredApifyRunId}
                  onChange={e => setApifyRunId(e.target.value)}
                  placeholder='Enter Apify run ID (e.g., mGx7opzdSMDEHLhPg)'
                />
                <p className='text-xs text-muted-foreground'>
                  Enter the Apify run ID directly. This allows processing VINs from any Apify run,
                  even if it's not in Step 1 execution history.
                </p>
              </div>
            )}

            {/* VIN Sampling Options */}
            <div className='space-y-4 pt-4 border-t'>
              <div className='space-y-2'>
                <Label htmlFor='configure-sampling-strategy'>VIN Sampling Strategy</Label>
                <Select
                  value={samplingStrategy}
                  onValueChange={(value: 'all' | 'random' | 'first' | 'last') =>
                    setSamplingStrategy(value)
                  }
                >
                  <SelectTrigger id='configure-sampling-strategy'>
                    <SelectValue placeholder='Select sampling strategy' />
                  </SelectTrigger>
                  <SelectContent
                    className='z-[10000]'
                    position='popper'
                    sideOffset={4}
                    style={{ zIndex: 10000 }}
                  >
                    <SelectItem value='all'>Process All VINs</SelectItem>
                    <SelectItem value='random'>Random Sample</SelectItem>
                    <SelectItem value='first'>First X VINs</SelectItem>
                    <SelectItem value='last'>Last X VINs</SelectItem>
                  </SelectContent>
                </Select>
                <p className='text-xs text-muted-foreground'>
                  Choose how to sample VINs for processing. Useful for testing or processing
                </p>
              </div>

              {samplingStrategy !== 'all' && (
                <div className='space-y-2'>
                  <Label htmlFor='configure-sampling-count'>Number of VINs to Sample *</Label>
                  <Input
                    id='configure-sampling-count'
                    type='number'
                    min={1}
                    max={100000}
                    value={samplingCount}
                    onChange={e => setSamplingCount(parseInt(e.target.value) || 100)}
                    placeholder='Enter number of VINs'
                  />
                  <p className='text-xs text-muted-foreground'>
                    {samplingStrategy === 'random'
                      ? 'Number of VINs to randomly select from the entry point.'
                      : samplingStrategy === 'first'
                        ? 'Number of VINs to process from the beginning.'
                        : 'Number of VINs to process from the end.'}
                  </p>
                  <div className='p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded text-xs text-amber-800 dark:text-amber-200'>
                    <strong>⚠️ Sampling Enabled:</strong> Only {samplingCount.toLocaleString()} VINs
                    will be processed, not all available VINs. Set strategy to "Process All VINs" to
                    process all VINs.
                  </div>
                </div>
              )}
              {samplingStrategy === 'all' && (
                <div className='p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded text-xs text-blue-800 dark:text-blue-200'>
                  <strong>✓ All VINs:</strong> All available VINs will be processed (no sampling
                  limit).
                </div>
              )}
            </div>

            {/* Force Re-run Option */}
            <div className='space-y-4 pt-4 border-t'>
              <div className='flex items-start space-x-3'>
                <Checkbox
                  id='configure-force-rerun'
                  checked={forceRerun}
                  onCheckedChange={checked => setForceRerun(checked === true)}
                />
                <div className='space-y-1 leading-none'>
                  <Label
                    htmlFor='configure-force-rerun'
                    className='text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer'
                  >
                    Force Re-run Failed Job
                  </Label>
                  <p className='text-xs text-muted-foreground'>
                    If enabled, re-triggers the same failed job instead of creating a new one. This
                    re-uses the existing job_id and VIN list for consistency. Useful for re-running
                    jobs that failed due to transient errors.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => {
                setConfigureDialogOpen(false);
                // Reset dialog state to configured values if canceling
                if (configuredInputMode === 'step1_execution' && configuredRunId) {
                  setInputMode('step1_execution');
                  setSelectedRunId(configuredRunId);
                } else if (configuredInputMode === 'apify_run_id' && configuredApifyRunId) {
                  setInputMode('apify_run_id');
                  setApifyRunId(configuredApifyRunId);
                } else if (
                  configuredInputMode === 'inventory_date_make' &&
                  configuredDate &&
                  configuredMake
                ) {
                  setInputMode('inventory_date_make');
                  setInventoryDate(configuredDate);
                  setSelectedMake(configuredMake);
                }
                setSamplingStrategy(configuredSamplingStrategy);
                setSamplingCount(configuredSamplingCount);
                setForceRerun(configuredForceRerun);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveConfiguration}
              disabled={
                (inputMode === 'inventory_date_make' && (!inventoryDate || !selectedMake)) ||
                (inputMode === 'step1_execution' && !selectedRunId) ||
                (inputMode === 'apify_run_id' && !apifyRunId) ||
                (samplingStrategy !== 'all' && (!samplingCount || samplingCount < 1))
              }
            >
              <CheckCircle className='h-4 w-4 mr-2' />
              Save Configuration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Process VINs Dialog */}
      <Dialog open={processDialogOpen} onOpenChange={setProcessDialogOpen}>
        <DialogContent className='max-w-2xl overflow-visible'>
          <DialogHeader>
            <DialogTitle>Step 2: Process VINs</DialogTitle>
            <DialogDescription>
              Choose how to select VINs for processing: by inventory date/make, Step 1 execution, or
              Apify run ID.
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-4 py-4'>
            {/* Entry Point Selection */}
            <div className='space-y-2'>
              <Label htmlFor='input-mode'>Entry Point *</Label>
              <Select
                value={inputMode}
                onValueChange={(
                  value: 'inventory_date_make' | 'step1_execution' | 'apify_run_id'
                ) => {
                  setInputMode(value);
                  // Clear other mode's selections when switching
                  if (value === 'inventory_date_make') {
                    setSelectedRunId('');
                    setApifyRunId('');
                  } else if (value === 'step1_execution') {
                    setInventoryDate(undefined);
                    setSelectedMake('');
                    setApifyRunId('');
                  } else {
                    setInventoryDate(undefined);
                    setSelectedMake('');
                    setSelectedRunId('');
                  }
                }}
              >
                <SelectTrigger id='input-mode'>
                  <SelectValue placeholder='Select entry point' />
                </SelectTrigger>
                <SelectContent
                  className='z-[10000]'
                  position='popper'
                  sideOffset={4}
                  style={{ zIndex: 10000 }}
                >
                  <SelectItem value='inventory_date_make'>1. Inventory Date + Make</SelectItem>
                  <SelectItem value='step1_execution'>2. Step 1 Execution</SelectItem>
                  <SelectItem value='apify_run_id'>3. Apify Run ID</SelectItem>
                </SelectContent>
              </Select>
              <p className='text-xs text-muted-foreground'>
                {inputMode === 'inventory_date_make'
                  ? 'Process all VINs collected on a specific date for a specific make.'
                  : inputMode === 'step1_execution'
                    ? 'Process VINs from a specific Step 1 data collection execution.'
                    : 'Process VINs from a specific Apify run ID (direct).'}
              </p>
            </div>

            {/* Entry Point 1: Inventory Date + Make */}
            {inputMode === 'inventory_date_make' && (
              <>
                <div className='space-y-2'>
                  <Label htmlFor='inventory-date'>Inventory Date *</Label>
                  <DatePicker
                    value={inventoryDate}
                    onChange={setInventoryDate}
                    placeholder='Select inventory date'
                    fromDate={new Date(2020, 0, 1)}
                    toDate={new Date()}
                  />
                  <p className='text-xs text-muted-foreground'>
                    Select the date when the inventory data was collected.
                  </p>
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='make-select'>Vehicle Make *</Label>
                  <Select value={selectedMake} onValueChange={setSelectedMake}>
                    <SelectTrigger id='make-select'>
                      <SelectValue placeholder='Select vehicle make' />
                    </SelectTrigger>
                    <SelectContent className='z-[10000]'>
                      {makeOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className='text-xs text-muted-foreground'>
                    Select the vehicle make to process (Ford/Lincoln, Hyundai, or Stellantis).
                  </p>
                </div>
              </>
            )}

            {/* Execution Mode */}
            {inputMode === 'step1_execution' && (
              <div className='space-y-2'>
                <Label htmlFor='execution-select'>Step 1 Execution *</Label>
                {completedStep1Executions.length === 0 ? (
                  <div className='text-sm text-muted-foreground p-4 border rounded-lg bg-muted/50'>
                    <AlertCircle className='h-4 w-4 inline mr-2' />
                    No completed Step 1 executions found. Please run Step 1 first.
                  </div>
                ) : (
                  <Select value={selectedRunId} onValueChange={setSelectedRunId}>
                    <SelectTrigger id='execution-select'>
                      <SelectValue placeholder='Select a Step 1 execution' />
                    </SelectTrigger>
                    <SelectContent className='z-[10000]'>
                      {completedStep1Executions.map((exec, index) => {
                        const execDate = exec.started_at ? new Date(exec.started_at) : new Date();
                        const oem = exec.oem || getOEMFromTaskName(exec.task_name || '');
                        const records =
                          exec.records_processed ||
                          exec.inventory_summary?.snapshot_inserted ||
                          exec.inventory_summary?.staging_inserted ||
                          0;

                        // Clean task name for display
                        let displayName = exec.task_name || 'Unknown Task';
                        displayName = displayName
                          .toLowerCase()
                          .replace(/^kenkebiai\/cargurus-/, '')
                          .replace(/-(stellantis|hyundai|ford|lincoln)-/g, '-')
                          .replace(/^(stellantis|hyundai|ford|lincoln)-/i, '')
                          .replace(/-(stellantis|hyundai|ford|lincoln)$/i, '')
                          .replace(/\b(stellantis|hyundai|ford|lincoln)\b/gi, '')
                          .replace(/-/g, ' ')
                          .replace(/\s+/g, ' ')
                          .trim()
                          .split(' ')
                          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                          .join(' ');

                        // Use exec.id (UUID) for value, not exec.run_id (Apify run ID)
                        // The backend expects UUID format for run_id according to OpenAPI spec
                        // Ensure we have a unique identifier - use id, run_id, or fallback to index
                        const executionId = exec.id || exec.run_id || `exec-${index}`;
                        // Create a truly unique key by combining executionId with index as fallback
                        const uniqueKey = executionId || `exec-${index}`;

                        return (
                          <SelectItem key={uniqueKey} value={executionId}>
                            <div className='flex flex-col'>
                              <span className='font-medium'>{displayName}</span>
                              <span className='text-xs text-muted-foreground'>
                                {execDate.toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}{' '}
                                • {oem} • {records.toLocaleString()} records
                              </span>
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                )}
                <p className='text-xs text-muted-foreground'>
                  Select a completed Step 1 execution to process its collected VINs.
                </p>
              </div>
            )}

            {/* Entry Point 3: Apify Run ID */}
            {inputMode === 'apify_run_id' && (
              <div className='space-y-2'>
                <Label htmlFor='apify-run-id'>Apify Run ID *</Label>
                <Input
                  id='apify-run-id'
                  value={apifyRunId}
                  onChange={e => setApifyRunId(e.target.value)}
                  placeholder='Enter Apify run ID (e.g., mGx7opzdSMDEHLhPg)'
                />
                <p className='text-xs text-muted-foreground'>
                  Enter the Apify run ID directly. This allows processing VINs from any Apify run,
                  even if it's not in Step 1 execution history.
                </p>
              </div>
            )}

            {/* VIN Sampling Options */}
            <div className='space-y-4 pt-4 border-t'>
              <div className='space-y-2'>
                <div className='flex items-center justify-between'>
                  <Label htmlFor='sampling-strategy'>VIN Sampling Strategy</Label>
                  {samplingStrategy !== 'all' && (
                    <Badge variant='destructive' className='text-xs'>
                      ⚠️ Sampling Active
                    </Badge>
                  )}
                  {samplingStrategy === 'all' && (
                    <Badge
                      variant='outline'
                      className='text-xs bg-green-50 text-green-700 border-green-200'
                    >
                      ✓ All VINs
                    </Badge>
                  )}
                </div>
                <Select
                  value={samplingStrategy}
                  onValueChange={(value: 'all' | 'random' | 'first' | 'last') =>
                    setSamplingStrategy(value)
                  }
                >
                  <SelectTrigger id='sampling-strategy'>
                    <SelectValue placeholder='Select sampling strategy' />
                  </SelectTrigger>
                  <SelectContent className='z-[10000]'>
                    <SelectItem value='all'>Process All VINs</SelectItem>
                    <SelectItem value='random'>Random Sample</SelectItem>
                    <SelectItem value='first'>First X VINs</SelectItem>
                    <SelectItem value='last'>Last X VINs</SelectItem>
                  </SelectContent>
                </Select>
                <p className='text-xs text-muted-foreground'>
                  Choose how to sample VINs for processing. Useful for testing or processing
                </p>
              </div>

              {samplingStrategy !== 'all' && (
                <div className='space-y-2'>
                  <Label htmlFor='sampling-count'>Number of VINs to Sample *</Label>
                  <Input
                    id='sampling-count'
                    type='number'
                    min={1}
                    max={100000}
                    value={samplingCount}
                    onChange={e => setSamplingCount(parseInt(e.target.value) || 100)}
                    placeholder='Enter number of VINs'
                  />
                  <p className='text-xs text-muted-foreground'>
                    {samplingStrategy === 'random'
                      ? 'Number of VINs to randomly select from the entry point.'
                      : samplingStrategy === 'first'
                        ? 'Number of VINs to process from the beginning.'
                        : 'Number of VINs to process from the end.'}
                  </p>
                  <div className='p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded text-xs text-amber-800 dark:text-amber-200'>
                    <strong>⚠️ Sampling Enabled:</strong> Only {samplingCount.toLocaleString()} VINs
                    will be processed, not all available VINs. Set strategy to "Process All VINs" to
                    process all{' '}
                    {inputMode === 'step1_execution' && selectedRunId
                      ? 'VINs from the selected Step 1 execution'
                      : inputMode === 'inventory_date_make'
                        ? 'VINs for the selected date and make'
                        : 'VINs'}
                    .
                  </div>
                </div>
              )}
              {samplingStrategy === 'all' && (
                <div className='p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded text-xs text-blue-800 dark:text-blue-200'>
                  <strong>✓ All VINs:</strong> All available VINs will be processed (no sampling
                  limit).
                </div>
              )}
            </div>

            {/* Force Re-run Option */}
            <div className='space-y-4 pt-4 border-t'>
              <div className='flex items-start space-x-3'>
                <Checkbox
                  id='force-rerun'
                  checked={forceRerun}
                  onCheckedChange={checked => setForceRerun(checked === true)}
                />
                <div className='space-y-1 leading-none'>
                  <Label
                    htmlFor='force-rerun'
                    className='text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer'
                  >
                    Force Re-run Failed Job
                  </Label>
                  <p className='text-xs text-muted-foreground'>
                    If enabled, re-triggers the same failed job instead of creating a new one. This
                    re-uses the existing job_id and VIN list for consistency. Useful for re-running
                    jobs that failed due to transient errors.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => setProcessDialogOpen(false)}
              disabled={createVINJobMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleProcessVINs}
              disabled={
                createVINJobMutation.isPending ||
                (inputMode === 'inventory_date_make' && (!inventoryDate || !selectedMake)) ||
                (inputMode === 'step1_execution' && !selectedRunId) ||
                (inputMode === 'apify_run_id' && !apifyRunId) ||
                (samplingStrategy !== 'all' && (!samplingCount || samplingCount < 1))
              }
            >
              {createVINJobMutation.isPending ? (
                <>
                  <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                  Processing...
                </>
              ) : (
                <>
                  <Play className='h-4 w-4 mr-2' />
                  Start Processing
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Re-run Confirmation Dialog */}
      <Dialog
        open={rerunConfirmDialogOpen}
        onOpenChange={open => {
          setRerunConfirmDialogOpen(open);
          if (!open) {
            setExecutionToRerun(null);
          }
        }}
      >
        <DialogContent className='max-w-2xl max-h-[80vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle>Confirm Re-run Execution</DialogTitle>
            <DialogDescription>
              Review the execution details and input parameters before re-running. This will create
              a new execution with the same parameters.
            </DialogDescription>
          </DialogHeader>
          {executionToRerun && (
            <div className='space-y-4 py-4'>
              {/* Execution Summary */}
              <div className='space-y-2'>
                <Label className='text-base font-semibold'>Execution Summary</Label>
                <div className='space-y-2 p-3 bg-muted/50 rounded-lg border'>
                  <div className='flex items-center justify-between'>
                    <span className='text-sm font-medium'>Workflow Step:</span>
                    <Badge
                      variant={
                        executionToRerun.executionType === 'data_collection'
                          ? 'default'
                          : 'secondary'
                      }
                    >
                      {executionToRerun.executionType === 'data_collection'
                        ? 'Step 1: Refresh Car Inventory'
                        : 'Step 2: Process VINs'}
                    </Badge>
                  </div>
                  {executionToRerun.task_name && (
                    <div className='flex items-center justify-between'>
                      <span className='text-sm font-medium'>Task:</span>
                      <span className='text-sm text-muted-foreground'>
                        {executionToRerun.task_name}
                      </span>
                    </div>
                  )}
                  {executionToRerun.run_id && (
                    <div className='flex items-center justify-between'>
                      <span className='text-sm font-medium'>Run ID:</span>
                      <span className='text-sm font-mono text-muted-foreground'>
                        {executionToRerun.run_id}
                      </span>
                    </div>
                  )}
                  {executionToRerun.job_id && (
                    <div className='flex items-center justify-between'>
                      <span className='text-sm font-medium'>Job ID:</span>
                      <span className='text-sm font-mono text-muted-foreground'>
                        {executionToRerun.job_id}
                      </span>
                    </div>
                  )}
                  {executionToRerun.started_at && (
                    <div className='flex items-center justify-between'>
                      <span className='text-sm font-medium'>Started:</span>
                      <span className='text-sm text-muted-foreground'>
                        {new Date(executionToRerun.started_at).toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Input Parameters */}
              <div className='space-y-2'>
                <Label className='text-base font-semibold'>Input Parameters</Label>
                <div className='p-3 bg-muted/50 rounded-lg border max-h-[300px] overflow-y-auto'>
                  {(() => {
                    const inputParams = executionToRerun['input_params'] || {};
                    const hasParams = Object.keys(inputParams).length > 0;

                    if (!hasParams && executionToRerun.executionType === 'data_collection') {
                      // For data collection, show task_name and other available fields
                      return (
                        <div className='space-y-2'>
                          {executionToRerun.task_name && (
                            <div>
                              <span className='text-sm font-medium'>Task Name: </span>
                              <span className='text-sm text-muted-foreground'>
                                {executionToRerun.task_name}
                              </span>
                            </div>
                          )}
                          {executionToRerun.oem && (
                            <div>
                              <span className='text-sm font-medium'>OEM: </span>
                              <span className='text-sm text-muted-foreground'>
                                {executionToRerun.oem}
                              </span>
                            </div>
                          )}
                          {executionToRerun.zone && (
                            <div>
                              <span className='text-sm font-medium'>Zone: </span>
                              <span className='text-sm text-muted-foreground'>
                                {executionToRerun.zone}
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    }
                    if (!hasParams) {
                      return (
                        <span className='text-sm text-muted-foreground'>
                          No input parameters available
                        </span>
                      );
                    }
                    return (
                      <div className='space-y-2'>
                        {Object.entries(inputParams).map(([key, value]) => {
                          let displayValue: string;
                          if (typeof value === 'object' && value !== null) {
                            displayValue = JSON.stringify(value, null, 2);
                          } else {
                            displayValue = String(value);
                          }
                          return (
                            <div
                              key={key}
                              className='border-b border-border/50 pb-2 last:border-0 last:pb-0'
                            >
                              <div className='text-sm font-medium mb-1'>{key}:</div>
                              <div className='text-sm text-muted-foreground font-mono bg-background p-2 rounded break-words'>
                                {displayValue}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Entry Point (for VIN Processing) */}
              {executionToRerun.executionType === 'vin_processing' &&
                executionToRerun.entry_point && (
                  <div className='space-y-2'>
                    <Label className='text-base font-semibold'>Entry Point</Label>
                    <div className='p-3 bg-muted/50 rounded-lg border'>
                      <span className='text-sm text-muted-foreground'>
                        {executionToRerun.entry_point === 'inventory_date_make'
                          ? 'Inventory Date + Make'
                          : executionToRerun.entry_point === 'step1_execution'
                            ? 'Step 1 Execution'
                            : executionToRerun.entry_point === 'apify_run_id'
                              ? 'Apify Run ID'
                              : executionToRerun.entry_point}
                      </span>
                    </div>
                  </div>
                )}
            </div>
          )}
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => {
                setRerunConfirmDialogOpen(false);
                setExecutionToRerun(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRerunExecution}
              disabled={runTaskMutation.isPending || createVINJobMutation.isPending}
            >
              {runTaskMutation.isPending || createVINJobMutation.isPending ? (
                <>
                  <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                  Re-running...
                </>
              ) : (
                <>
                  <RotateCw className='h-4 w-4 mr-2' />
                  Confirm Re-run
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
